import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
    LayoutDashboard, Settings, Tag, Users, BarChart2, 
    LogOut, Plus, Trash2, Download, RefreshCw,
    DollarSign, Eye, TrendingUp, CheckCircle, Calendar,
    Package, ChevronDown, ExternalLink, FileText, RotateCcw,
    Upload, Link, File, Mail, Copy, Ban, AlertTriangle,
    MoreVertical, Send, X, Globe
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../components/ui/popover";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogDescription,
} from "../components/ui/dialog";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { toast } from "sonner";
import axios from "axios";
import Cookies from "js-cookie";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Section Editor Component - moved outside to prevent re-renders
const SectionEditor = ({ sectionName, title, children, isCustomized, onSave, onReset }) => (
    <AccordionItem value={sectionName} className="bg-card border border-border mb-4">
        <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-center justify-between w-full pr-4">
                <div className="flex items-center gap-3">
                    <span className="font-heading font-bold">{title}</span>
                    {isCustomized && (
                        <span className="text-xs bg-primary/20 text-primary px-2 py-1">Customized</span>
                    )}
                </div>
            </div>
        </AccordionTrigger>
        <AccordionContent className="px-6 pb-6">
            <div className="space-y-4">
                {children}
                <div className="flex gap-2 pt-4 border-t border-border">
                    <Button 
                        onClick={onSave}
                        className="btn-primary"
                    >
                        Save {title}
                    </Button>
                    <Button 
                        onClick={onReset}
                        variant="outline"
                        className="border-border"
                    >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Reset to Default
                    </Button>
                </div>
            </div>
        </AccordionContent>
    </AccordionItem>
);

// Site URL Settings Component (for email links)
const SiteUrlSettingsCard = ({ authConfig }) => {
    const [siteUrl, setSiteUrl] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await axios.get(`${API}/admin/defaults`, authConfig);
                setSiteUrl(res.data.site_url || "");
            } catch (e) {
                console.error("Error fetching site URL:", e);
            }
            setIsLoading(false);
        };
        if (authConfig) {
            fetchSettings();
        }
    }, [authConfig]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await axios.put(`${API}/admin/defaults`, { site_url: siteUrl }, authConfig);
            toast.success("Site URL saved - Email links will now use this URL");
        } catch (e) {
            toast.error("Failed to save site URL");
        }
        setIsSaving(false);
    };

    if (isLoading) return <div className="animate-pulse h-32 bg-secondary/30" />;

    return (
        <div className="bg-card border border-border p-6">
            <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                Site URL (for Email Links)
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
                Configure the base URL used in all email links. <strong>Important:</strong> Set this to your production domain (e.g., https://yourdomain.com) to ensure email links work correctly.
            </p>
            <div>
                <Label className="text-sm text-muted-foreground">Site URL</Label>
                <Input
                    type="url"
                    placeholder="https://yourdomain.com"
                    value={siteUrl}
                    onChange={(e) => setSiteUrl(e.target.value)}
                    className="mt-1 bg-input border-border"
                />
                <p className="text-xs text-muted-foreground mt-1">
                    Leave empty to use system default. Include https:// and no trailing slash.
                </p>
            </div>
            <Button 
                onClick={handleSave}
                disabled={isSaving}
                className="btn-primary mt-4"
            >
                {isSaving ? "Saving..." : "Save Site URL"}
            </Button>
        </div>
    );
};

// Download Settings Component
const DownloadSettingsCard = ({ authConfig }) => {
    const [downloadSettings, setDownloadSettings] = useState({ expires_hours: 72, max_downloads: 5 });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await axios.get(`${API}/admin/settings/downloads`, authConfig);
                setDownloadSettings(res.data);
            } catch (e) {
                console.error("Error fetching download settings:", e);
            }
            setIsLoading(false);
        };
        if (authConfig) {
            fetchSettings();
        }
    }, [authConfig]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await axios.put(
                `${API}/admin/settings/downloads?expires_hours=${downloadSettings.expires_hours}&max_downloads=${downloadSettings.max_downloads}`,
                {},
                authConfig
            );
            toast.success("Download settings saved");
        } catch (e) {
            toast.error("Failed to save settings");
        }
        setIsSaving(false);
    };

    if (isLoading) return <div className="animate-pulse h-32 bg-secondary/30" />;

    return (
        <div className="bg-card border border-border p-6">
            <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                <Download className="w-5 h-5 text-primary" />
                Download Link Settings
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
                Configure how long download links remain valid and how many times customers can download.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
                <div>
                    <Label className="text-sm text-muted-foreground">Link Expiration (hours)</Label>
                    <Input
                        type="number"
                        min={1}
                        max={720}
                        value={downloadSettings.expires_hours}
                        onChange={(e) => setDownloadSettings({ ...downloadSettings, expires_hours: parseInt(e.target.value) || 72 })}
                        className="mt-1 bg-input border-border"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                        {downloadSettings.expires_hours} hours = {(downloadSettings.expires_hours / 24).toFixed(1)} days
                    </p>
                </div>
                <div>
                    <Label className="text-sm text-muted-foreground">Max Downloads per Purchase</Label>
                    <Input
                        type="number"
                        min={1}
                        max={100}
                        value={downloadSettings.max_downloads}
                        onChange={(e) => setDownloadSettings({ ...downloadSettings, max_downloads: parseInt(e.target.value) || 5 })}
                        className="mt-1 bg-input border-border"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                        Customers can download their files {downloadSettings.max_downloads} times
                    </p>
                </div>
            </div>
            <Button 
                onClick={handleSave}
                disabled={isSaving}
                className="btn-primary mt-4"
            >
                {isSaving ? "Saving..." : "Save Download Settings"}
            </Button>
        </div>
    );
};

// Email Template Settings Component
const EmailSettingsCard = ({ authConfig }) => {
    const [emailSettings, setEmailSettings] = useState({
        subject: "Your Download: {product_name}",
        heading: "Thank You for Your Purchase!",
        body: "Your copy of {product_name} is ready for download.",
        button_text: "Download Your Guide",
        footer: "If you have any questions, simply reply to this email."
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await axios.get(`${API}/admin/settings/email`, authConfig);
                setEmailSettings(res.data);
            } catch (e) {
                console.error("Error fetching email settings:", e);
            }
            setIsLoading(false);
        };
        if (authConfig) {
            fetchSettings();
        }
    }, [authConfig]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await axios.put(`${API}/admin/settings/email`, emailSettings, authConfig);
            toast.success("Email template saved");
        } catch (e) {
            toast.error("Failed to save email template");
        }
        setIsSaving(false);
    };

    if (isLoading) return <div className="animate-pulse h-64 bg-secondary/30" />;

    return (
        <div className="bg-card border border-border p-6">
            <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Purchase Email Template
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
                Customize the email sent to customers after purchase. Use <code className="bg-secondary px-1 rounded">{"{product_name}"}</code> as a placeholder.
            </p>
            <div className="space-y-4">
                <div>
                    <Label className="text-sm text-muted-foreground">Email Subject</Label>
                    <Input
                        value={emailSettings.subject}
                        onChange={(e) => setEmailSettings({ ...emailSettings, subject: e.target.value })}
                        className="mt-1 bg-input border-border"
                        placeholder="Your Download: {product_name}"
                    />
                </div>
                <div>
                    <Label className="text-sm text-muted-foreground">Heading</Label>
                    <Input
                        value={emailSettings.heading}
                        onChange={(e) => setEmailSettings({ ...emailSettings, heading: e.target.value })}
                        className="mt-1 bg-input border-border"
                        placeholder="Thank You for Your Purchase!"
                    />
                </div>
                <div>
                    <Label className="text-sm text-muted-foreground">Body Text</Label>
                    <Textarea
                        value={emailSettings.body}
                        onChange={(e) => setEmailSettings({ ...emailSettings, body: e.target.value })}
                        className="mt-1 bg-input border-border"
                        placeholder="Your copy of {product_name} is ready for download."
                        rows={3}
                    />
                </div>
                <div>
                    <Label className="text-sm text-muted-foreground">Download Button Text</Label>
                    <Input
                        value={emailSettings.button_text}
                        onChange={(e) => setEmailSettings({ ...emailSettings, button_text: e.target.value })}
                        className="mt-1 bg-input border-border"
                        placeholder="Download Your Guide"
                    />
                </div>
                <div>
                    <Label className="text-sm text-muted-foreground">Footer Text</Label>
                    <Textarea
                        value={emailSettings.footer}
                        onChange={(e) => setEmailSettings({ ...emailSettings, footer: e.target.value })}
                        className="mt-1 bg-input border-border"
                        placeholder="If you have any questions, simply reply to this email."
                        rows={2}
                    />
                </div>
            </div>
            <Button 
                onClick={handleSave}
                disabled={isSaving}
                className="btn-primary mt-4"
            >
                {isSaving ? "Saving..." : "Save Email Template"}
            </Button>
        </div>
    );
};

// Abandoned Cart Email Settings Component
const AbandonedCartEmailCard = ({ authConfig, productSettings }) => {
    const [settings, setSettings] = useState({
        subject: "Did you forget something?",
        heading: "Your cart is waiting...",
        body: "You were just about to get {product_name}. Use code {coupon_code} to complete your purchase!",
        button_text: "Complete My Order"
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewHtml, setPreviewHtml] = useState("");
    const [testEmail, setTestEmail] = useState("");
    const [isSendingTest, setIsSendingTest] = useState(false);

    // Determine which coupon this email uses
    const getCouponInfo = () => {
        if (productSettings?.abandoned_cart_coupon_code) {
            return { code: productSettings.abandoned_cart_coupon_code, source: "Abandoned Cart Coupon" };
        }
        if (productSettings?.sections?.exit_popup?.coupon_code) {
            return { code: productSettings.sections.exit_popup.coupon_code, source: "Popup Coupon (fallback)" };
        }
        return { code: "COMEBACK10", source: "Default" };
    };
    const couponInfo = getCouponInfo();

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await axios.get(`${API}/admin/settings/abandoned-cart`, authConfig);
                setSettings(res.data);
            } catch (e) {
                console.error("Error fetching abandoned cart settings:", e);
            }
            setIsLoading(false);
        };
        if (authConfig) fetchSettings();
    }, [authConfig]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await axios.put(`${API}/admin/settings/abandoned-cart`, settings, authConfig);
            toast.success("Abandoned cart email saved");
        } catch (e) {
            toast.error("Failed to save");
        }
        setIsSaving(false);
    };

    const handlePreview = async () => {
        try {
            const res = await axios.get(`${API}/admin/email/preview/abandoned_cart`, authConfig);
            setPreviewHtml(res.data.html);
            setShowPreview(true);
        } catch (e) {
            toast.error("Failed to load preview");
        }
    };

    const handleSendTest = async () => {
        if (!testEmail) {
            toast.error("Please enter a test email");
            return;
        }
        setIsSendingTest(true);
        try {
            await axios.post(`${API}/admin/email/test?template_type=abandoned_cart&test_email=${encodeURIComponent(testEmail)}`, {}, authConfig);
            toast.success(`Test email sent to ${testEmail}`);
            setTestEmail("");
        } catch (e) {
            toast.error("Failed to send test email");
        }
        setIsSendingTest(false);
    };

    if (isLoading) return <div className="animate-pulse h-48 bg-secondary/30" />;

    return (
        <div className="bg-card border border-border p-6">
            <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Abandoned Cart Email
            </h3>
            <p className="text-sm text-muted-foreground mb-2">
                Sent to customers who start checkout but don't complete. Available variables:
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
                <code className="bg-secondary px-2 py-1 rounded text-xs">{"{product_name}"}</code>
                <code className="bg-secondary px-2 py-1 rounded text-xs">{"{coupon_code}"}</code>
                <code className="bg-secondary px-2 py-1 rounded text-xs">{"{discount_percent}"}</code>
                <code className="bg-secondary px-2 py-1 rounded text-xs">{"{link}"}</code>
            </div>
            <div className="bg-primary/10 border border-primary/30 rounded p-3 mb-4">
                <p className="text-sm">
                    <strong>Coupon usato:</strong> <code className="bg-secondary px-2 py-1 rounded">{couponInfo.code}</code>
                    <span className="text-muted-foreground ml-2">({couponInfo.source})</span>
                </p>
            </div>
            <div className="space-y-4">
                <div>
                    <Label className="text-sm text-muted-foreground">Subject</Label>
                    <Input value={settings.subject} onChange={(e) => setSettings({...settings, subject: e.target.value})} className="mt-1 bg-input border-border" />
                </div>
                <div>
                    <Label className="text-sm text-muted-foreground">Heading</Label>
                    <Input value={settings.heading} onChange={(e) => setSettings({...settings, heading: e.target.value})} className="mt-1 bg-input border-border" />
                </div>
                <div>
                    <Label className="text-sm text-muted-foreground">Body</Label>
                    <Textarea value={settings.body} onChange={(e) => setSettings({...settings, body: e.target.value})} className="mt-1 bg-input border-border" rows={3} />
                </div>
                <div>
                    <Label className="text-sm text-muted-foreground">Button Text</Label>
                    <Input value={settings.button_text} onChange={(e) => setSettings({...settings, button_text: e.target.value})} className="mt-1 bg-input border-border" />
                </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
                <Button onClick={handleSave} disabled={isSaving} className="btn-primary">
                    {isSaving ? "Saving..." : "Save"}
                </Button>
                <Button onClick={handlePreview} variant="outline" className="border-border">
                    <Eye className="w-4 h-4 mr-2" /> Preview
                </Button>
            </div>
            <div className="flex gap-2 mt-4">
                <Input 
                    placeholder="Test email address" 
                    value={testEmail} 
                    onChange={(e) => setTestEmail(e.target.value)} 
                    className="bg-input border-border flex-1"
                />
                <Button onClick={handleSendTest} disabled={isSendingTest} variant="outline" className="border-border">
                    <Send className="w-4 h-4 mr-2" /> {isSendingTest ? "Sending..." : "Send Test"}
                </Button>
            </div>

            {/* Preview Modal */}
            {showPreview && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
                    <div className="bg-card max-w-2xl w-full max-h-[80vh] overflow-auto relative" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setShowPreview(false)} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
                            <X className="w-6 h-6" />
                        </button>
                        <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                    </div>
                </div>
            )}
        </div>
    );
};

// Popup Email Settings Component
const PopupEmailCard = ({ authConfig, productSettings }) => {
    const [settings, setSettings] = useState({
        subject: "Thanks for your interest in {product_name}",
        heading: "Thanks for your interest!",
        body: "You've shown interest in {product_name}. Use code {coupon_code} for a special discount!"
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewHtml, setPreviewHtml] = useState("");
    const [testEmail, setTestEmail] = useState("");
    const [isSendingTest, setIsSendingTest] = useState(false);

    // Determine which coupon this email uses
    const getCouponInfo = () => {
        if (productSettings?.sections?.exit_popup?.coupon_code) {
            return { code: productSettings.sections.exit_popup.coupon_code, source: "Popup Coupon" };
        }
        return { code: "COMEBACK10", source: "Default" };
    };
    const couponInfo = getCouponInfo();

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await axios.get(`${API}/admin/settings/popup-email`, authConfig);
                setSettings(res.data);
            } catch (e) {
                console.error("Error fetching popup email settings:", e);
            }
            setIsLoading(false);
        };
        if (authConfig) fetchSettings();
    }, [authConfig]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await axios.put(`${API}/admin/settings/popup-email`, settings, authConfig);
            toast.success("Popup email saved");
        } catch (e) {
            toast.error("Failed to save");
        }
        setIsSaving(false);
    };

    const handlePreview = async () => {
        try {
            const res = await axios.get(`${API}/admin/email/preview/popup`, authConfig);
            setPreviewHtml(res.data.html);
            setShowPreview(true);
        } catch (e) {
            toast.error("Failed to load preview");
        }
    };

    const handleSendTest = async () => {
        if (!testEmail) {
            toast.error("Please enter a test email");
            return;
        }
        setIsSendingTest(true);
        try {
            await axios.post(`${API}/admin/email/test?template_type=popup&test_email=${encodeURIComponent(testEmail)}`, {}, authConfig);
            toast.success(`Test email sent to ${testEmail}`);
            setTestEmail("");
        } catch (e) {
            toast.error("Failed to send test email");
        }
        setIsSendingTest(false);
    };

    if (isLoading) return <div className="animate-pulse h-48 bg-secondary/30" />;

    return (
        <div className="bg-card border border-border p-6">
            <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Popup Lead Email
            </h3>
            <p className="text-sm text-muted-foreground mb-2">
                Sent to leads who submit their email via exit/welcome popup. Available variables:
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
                <code className="bg-secondary px-2 py-1 rounded text-xs">{"{product_name}"}</code>
                <code className="bg-secondary px-2 py-1 rounded text-xs">{"{coupon_code}"}</code>
                <code className="bg-secondary px-2 py-1 rounded text-xs">{"{discount_percent}"}</code>
                <code className="bg-secondary px-2 py-1 rounded text-xs">{"{link}"}</code>
            </div>
            <div className="bg-primary/10 border border-primary/30 rounded p-3 mb-4">
                <p className="text-sm">
                    <strong>Coupon usato:</strong> <code className="bg-secondary px-2 py-1 rounded">{couponInfo.code}</code>
                    <span className="text-muted-foreground ml-2">({couponInfo.source})</span>
                </p>
            </div>
            <div className="space-y-4">
                <div>
                    <Label className="text-sm text-muted-foreground">Subject</Label>
                    <Input value={settings.subject} onChange={(e) => setSettings({...settings, subject: e.target.value})} className="mt-1 bg-input border-border" />
                </div>
                <div>
                    <Label className="text-sm text-muted-foreground">Heading</Label>
                    <Input value={settings.heading} onChange={(e) => setSettings({...settings, heading: e.target.value})} className="mt-1 bg-input border-border" />
                </div>
                <div>
                    <Label className="text-sm text-muted-foreground">Body</Label>
                    <Textarea value={settings.body} onChange={(e) => setSettings({...settings, body: e.target.value})} className="mt-1 bg-input border-border" rows={3} />
                </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
                <Button onClick={handleSave} disabled={isSaving} className="btn-primary">
                    {isSaving ? "Saving..." : "Save"}
                </Button>
                <Button onClick={handlePreview} variant="outline" className="border-border">
                    <Eye className="w-4 h-4 mr-2" /> Preview
                </Button>
            </div>
            <div className="flex gap-2 mt-4">
                <Input 
                    placeholder="Test email address" 
                    value={testEmail} 
                    onChange={(e) => setTestEmail(e.target.value)} 
                    className="bg-input border-border flex-1"
                />
                <Button onClick={handleSendTest} disabled={isSendingTest} variant="outline" className="border-border">
                    <Send className="w-4 h-4 mr-2" /> {isSendingTest ? "Sending..." : "Send Test"}
                </Button>
            </div>

            {/* Preview Modal */}
            {showPreview && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
                    <div className="bg-card max-w-2xl w-full max-h-[80vh] overflow-auto relative" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setShowPreview(false)} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
                            <X className="w-6 h-6" />
                        </button>
                        <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                    </div>
                </div>
            )}
        </div>
    );
};

// Recall Email Settings Component
const RecallEmailCard = ({ authConfig, productSettings }) => {
    const [settings, setSettings] = useState({
        subject: "We miss you! Here's a special offer",
        heading: "Don't Miss Out!",
        body: "We noticed you were interested in {product_name}. Come back and use code {coupon_code} for an exclusive discount!",
        button_text: "Claim My Discount"
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewHtml, setPreviewHtml] = useState("");
    const [testEmail, setTestEmail] = useState("");
    const [isSendingTest, setIsSendingTest] = useState(false);

    // Determine which coupon this email uses
    const getCouponInfo = () => {
        if (productSettings?.recall_coupon_code) {
            return { code: productSettings.recall_coupon_code, source: "Recall Coupon" };
        }
        if (productSettings?.abandoned_cart_coupon_code) {
            return { code: productSettings.abandoned_cart_coupon_code, source: "Abandoned Cart Coupon (fallback)" };
        }
        if (productSettings?.sections?.exit_popup?.coupon_code) {
            return { code: productSettings.sections.exit_popup.coupon_code, source: "Popup Coupon (fallback)" };
        }
        return { code: "COMEBACK10", source: "Default" };
    };
    const couponInfo = getCouponInfo();

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await axios.get(`${API}/admin/settings/recall-email`, authConfig);
                setSettings(res.data);
            } catch (e) {
                console.error("Error fetching recall email settings:", e);
            }
            setIsLoading(false);
        };
        if (authConfig) fetchSettings();
    }, [authConfig]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await axios.put(`${API}/admin/settings/recall-email`, settings, authConfig);
            toast.success("Recall email saved");
        } catch (e) {
            toast.error("Failed to save");
        }
        setIsSaving(false);
    };

    const handlePreview = async () => {
        try {
            const res = await axios.get(`${API}/admin/email/preview/recall`, authConfig);
            setPreviewHtml(res.data.html);
            setShowPreview(true);
        } catch (e) {
            toast.error("Failed to load preview");
        }
    };

    const handleSendTest = async () => {
        if (!testEmail) {
            toast.error("Please enter a test email");
            return;
        }
        setIsSendingTest(true);
        try {
            await axios.post(`${API}/admin/email/test?template_type=recall&test_email=${encodeURIComponent(testEmail)}`, {}, authConfig);
            toast.success(`Test email sent to ${testEmail}`);
            setTestEmail("");
        } catch (e) {
            toast.error("Failed to send test email");
        }
        setIsSendingTest(false);
    };

    if (isLoading) return <div className="animate-pulse h-48 bg-secondary/30" />;

    return (
        <div className="bg-card border border-border p-6">
            <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Recall Email (Re-engagement)
            </h3>
            <p className="text-sm text-muted-foreground mb-2">
                Manually send to leads to re-engage them. Available variables:
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
                <code className="bg-secondary px-2 py-1 rounded text-xs">{"{product_name}"}</code>
                <code className="bg-secondary px-2 py-1 rounded text-xs">{"{coupon_code}"}</code>
                <code className="bg-secondary px-2 py-1 rounded text-xs">{"{discount_percent}"}</code>
                <code className="bg-secondary px-2 py-1 rounded text-xs">{"{link}"}</code>
            </div>
            <div className="bg-primary/10 border border-primary/30 rounded p-3 mb-4">
                <p className="text-sm">
                    <strong>Coupon usato:</strong> <code className="bg-secondary px-2 py-1 rounded">{couponInfo.code}</code>
                    <span className="text-muted-foreground ml-2">({couponInfo.source})</span>
                </p>
            </div>
            <div className="space-y-4">
                <div>
                    <Label className="text-sm text-muted-foreground">Subject</Label>
                    <Input value={settings.subject} onChange={(e) => setSettings({...settings, subject: e.target.value})} className="mt-1 bg-input border-border" />
                </div>
                <div>
                    <Label className="text-sm text-muted-foreground">Heading</Label>
                    <Input value={settings.heading} onChange={(e) => setSettings({...settings, heading: e.target.value})} className="mt-1 bg-input border-border" />
                </div>
                <div>
                    <Label className="text-sm text-muted-foreground">Body</Label>
                    <Textarea value={settings.body} onChange={(e) => setSettings({...settings, body: e.target.value})} className="mt-1 bg-input border-border" rows={3} />
                </div>
                <div>
                    <Label className="text-sm text-muted-foreground">Button Text</Label>
                    <Input value={settings.button_text} onChange={(e) => setSettings({...settings, button_text: e.target.value})} className="mt-1 bg-input border-border" />
                </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
                <Button onClick={handleSave} disabled={isSaving} className="btn-primary">
                    {isSaving ? "Saving..." : "Save"}
                </Button>
                <Button onClick={handlePreview} variant="outline" className="border-border">
                    <Eye className="w-4 h-4 mr-2" /> Preview
                </Button>
            </div>
            <div className="flex gap-2 mt-4">
                <Input 
                    placeholder="Test email address" 
                    value={testEmail} 
                    onChange={(e) => setTestEmail(e.target.value)} 
                    className="bg-input border-border flex-1"
                />
                <Button onClick={handleSendTest} disabled={isSendingTest} variant="outline" className="border-border">
                    <Send className="w-4 h-4 mr-2" /> {isSendingTest ? "Sending..." : "Send Test"}
                </Button>
            </div>

            {/* Preview Modal */}
            {showPreview && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
                    <div className="bg-card max-w-2xl w-full max-h-[80vh] overflow-auto relative" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setShowPreview(false)} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
                            <X className="w-6 h-6" />
                        </button>
                        <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                    </div>
                </div>
            )}
        </div>
    );
};

// PDF Upload Field Component - allows URL input or file upload
const PdfUploadField = ({ value, onChange, authConfig }) => {
    const [mode, setMode] = useState(value && !value.includes('/api/files/') ? 'url' : 'upload');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const fileInputRef = useRef(null);

    // Determine if current value is an uploaded file
    const isUploadedFile = value && value.includes('/api/files/');
    const fileName = isUploadedFile ? value.split('/').pop().split('_').slice(1).join('_') : null;

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.name.toLowerCase().endsWith('.pdf')) {
            toast.error("Only PDF files are allowed");
            return;
        }

        if (file.size > 50 * 1024 * 1024) {
            toast.error("File size exceeds 50MB limit");
            return;
        }

        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post(`${API}/admin/upload/pdf`, formData, {
                ...authConfig,
                headers: {
                    ...authConfig.headers,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                const fileUrl = `${API}/files/${response.data.file_id}`;
                setUploadedFile({ name: file.name, id: response.data.file_id });
                onChange(fileUrl);
                toast.success(`Uploaded: ${file.name}`);
            }
        } catch (err) {
            toast.error("Failed to upload PDF");
            console.error(err);
        }
        setIsUploading(false);
    };

    return (
        <div className="mt-2 space-y-3">
            {/* Mode Toggle */}
            <div className="flex gap-2">
                <Button
                    type="button"
                    variant={mode === 'url' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMode('url')}
                    className={mode === 'url' ? 'btn-primary' : 'border-border'}
                >
                    <Link className="w-4 h-4 mr-2" />
                    Use URL
                </Button>
                <Button
                    type="button"
                    variant={mode === 'upload' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMode('upload')}
                    className={mode === 'upload' ? 'btn-primary' : 'border-border'}
                >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload PDF
                </Button>
            </div>

            {/* URL Input Mode */}
            {mode === 'url' && (
                <Input
                    value={isUploadedFile ? '' : value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="https://example.com/your-ebook.pdf"
                    className="bg-input border-border"
                />
            )}

            {/* Upload Mode */}
            {mode === 'upload' && (
                <div className="space-y-2">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                    />
                    
                    {isUploadedFile || uploadedFile ? (
                        <div className="flex items-center gap-3 p-3 bg-secondary/30 border border-border rounded">
                            <File className="w-8 h-8 text-primary" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                    {uploadedFile?.name || fileName}
                                </p>
                                <p className="text-xs text-muted-foreground">PDF uploaded</p>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isUploading}
                                className="border-border"
                            >
                                Replace
                            </Button>
                        </div>
                    ) : (
                        <div 
                            onClick={() => fileInputRef.current?.click()}
                            className="border-2 border-dashed border-border rounded p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                        >
                            {isUploading ? (
                                <div className="flex flex-col items-center gap-2">
                                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                    <p className="text-sm text-muted-foreground">Uploading...</p>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2">
                                    <Upload className="w-8 h-8 text-muted-foreground" />
                                    <p className="text-sm text-muted-foreground">
                                        Click to upload PDF (max 50MB)
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Current value display */}
            {value && (
                <p className="text-xs text-muted-foreground truncate">
                    Current: {isUploadedFile ? `Uploaded file (${fileName})` : value}
                </p>
            )}
        </div>
    );
};

// Reset Stats Button Component
const ResetStatsButton = ({ productTag, authConfig, onReset }) => {
    const [showConfirm, setShowConfirm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleReset = async () => {
        setIsLoading(true);
        try {
            const res = await axios.delete(
                `${API}/admin/products/${productTag}/stats/reset?confirm=RESET`,
                authConfig
            );
            toast.success(`Reset complete: ${res.data.deleted.page_views} page views, ${res.data.deleted.funnel_events} funnel events deleted`);
            setShowConfirm(false);
            onReset();
        } catch (err) {
            toast.error("Failed to reset stats");
        }
        setIsLoading(false);
    };

    return (
        <>
            <Button 
                variant="outline" 
                onClick={() => setShowConfirm(true)}
                className="border-border text-muted-foreground hover:text-destructive hover:border-destructive"
            >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset Stats
            </Button>

            <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
                <AlertDialogContent className="bg-card border-border">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                            <AlertTriangle className="w-5 h-5" />
                            Reset All Stats
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to reset all statistics for this product?
                            <br /><br />
                            This will permanently delete:
                            <ul className="list-disc list-inside mt-2 text-sm">
                                <li>All page view records</li>
                                <li>All funnel events (checkout started, completed, etc.)</li>
                                <li>Traffic and conversion data</li>
                            </ul>
                            <br />
                            <strong>Note:</strong> Customer records and sales data will NOT be deleted.
                            <br /><br />
                            <strong className="text-destructive">This action cannot be undone.</strong>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleReset}
                            disabled={isLoading}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {isLoading ? "Resetting..." : "Yes, Reset All Stats"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

// Lead Actions Menu Component (for customer-paid leads)
const LeadActionsMenu = ({ lead, authConfig, onRefresh }) => {
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleCopyDownloadLink = async () => {
        // Fetch download tokens for this lead
        try {
            const res = await axios.get(`${API}/admin/leads/${lead.id}/downloads`, authConfig);
            const tokens = res.data;
            const activeToken = tokens.find(t => !t.disabled && new Date(t.expires_at) > new Date());
            if (activeToken) {
                const link = `${window.location.origin}/download/${activeToken.token}`;
                await navigator.clipboard.writeText(link);
                toast.success("Download link copied to clipboard");
            } else {
                toast.error("No active download link found");
            }
        } catch (err) {
            toast.error("Failed to get download link");
        }
    };

    const handleResendEmail = async () => {
        setIsLoading(true);
        try {
            await axios.post(`${API}/admin/leads/${lead.id}/resend-email`, {}, authConfig);
            toast.success(`Email sent to ${lead.email}`);
        } catch (err) {
            toast.error("Failed to send email");
        }
        setIsLoading(false);
    };

    const handleRegenerateLink = async () => {
        setIsLoading(true);
        try {
            await axios.post(`${API}/admin/leads/${lead.id}/downloads/refresh`, {}, authConfig);
            toast.success("New download link generated");
            onRefresh();
        } catch (err) {
            toast.error("Failed to regenerate link");
        }
        setIsLoading(false);
    };

    const handleDisableLinks = async () => {
        setIsLoading(true);
        try {
            await axios.post(`${API}/admin/leads/${lead.id}/downloads/disable`, {}, authConfig);
            toast.success("Download links disabled");
            onRefresh();
        } catch (err) {
            toast.error("Failed to disable links");
        }
        setIsLoading(false);
    };

    const handleDeleteLead = async () => {
        setIsLoading(true);
        try {
            await axios.delete(`${API}/admin/leads/${lead.id}?confirm=DELETE`, authConfig);
            toast.success("Lead deleted");
            setShowDeleteConfirm(false);
            onRefresh();
        } catch (err) {
            toast.error("Failed to delete lead");
        }
        setIsLoading(false);
    };

    // Only show customer-paid specific actions for paid leads
    const isPaid = lead.status === "customer-paid";

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-card border-border">
                    {isPaid && (
                        <>
                            <DropdownMenuItem onClick={handleCopyDownloadLink} className="cursor-pointer">
                                <Copy className="mr-2 h-4 w-4" />
                                Copy Download Link
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleResendEmail} disabled={isLoading} className="cursor-pointer">
                                <Mail className="mr-2 h-4 w-4" />
                                Resend Purchase Email
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-border" />
                            <DropdownMenuItem onClick={handleRegenerateLink} disabled={isLoading} className="cursor-pointer">
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Regenerate Link
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleDisableLinks} disabled={isLoading} className="cursor-pointer text-yellow-500">
                                <Ban className="mr-2 h-4 w-4" />
                                Disable Links
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-border" />
                        </>
                    )}
                    <DropdownMenuItem 
                        onClick={() => setShowDeleteConfirm(true)} 
                        className="cursor-pointer text-destructive focus:text-destructive"
                    >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete Lead
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent className="bg-card border-border">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                            <AlertTriangle className="w-5 h-5" />
                            Delete Lead
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete <strong>{lead.email}</strong>?
                            <br /><br />
                            This will permanently delete:
                            <ul className="list-disc list-inside mt-2 text-sm">
                                <li>Lead record</li>
                                {isPaid && <li>All download links</li>}
                                <li>Contact from Resend (if applicable)</li>
                            </ul>
                            <br />
                            <strong className="text-destructive">This action cannot be undone.</strong>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDeleteLead}
                            disabled={isLoading}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {isLoading ? "Deleting..." : "Yes, Delete Lead"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

const AdminDashboard = () => {
    const { productTag: urlProductTag } = useParams();
    const [activeTab, setActiveTab] = useState("dashboard");
    const [isLoading, setIsLoading] = useState(true);
    
    // Products
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showProductSelector, setShowProductSelector] = useState(false);
    const [showNewProductDialog, setShowNewProductDialog] = useState(false);
    const [showCloneDialog, setShowCloneDialog] = useState(false);
    const [cloneNewTag, setCloneNewTag] = useState("");
    const [newProduct, setNewProduct] = useState({ tag: "", name: "", base_price: 47, discount_price: 27, pdf_url: "" });
    
    // Product-specific data
    const [stats, setStats] = useState(null);
    const [productSettings, setProductSettings] = useState(null);
    const [coupons, setCoupons] = useState([]);
    const [leads, setLeads] = useState([]);
    const [funnel, setFunnel] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    
    // Sections editing
    const [editingSections, setEditingSections] = useState(null);
    const [expandedSection, setExpandedSection] = useState(null);
    
    // Global defaults
    const [globalDefaults, setGlobalDefaults] = useState(null);
    const [editingDefaults, setEditingDefaults] = useState(null);
    
    // Bump stats
    const [bumpStats, setBumpStats] = useState(null);
    
    const [newCoupon, setNewCoupon] = useState({ code: "", discount_percent: 10, max_uses: null });
    const [newTestimonial, setNewTestimonial] = useState({ name: "", age: 30, text: "", rating: 5 });
    
    // Date range filter state
    const [dateRange, setDateRange] = useState({ from: null, to: null });
    const [showDatePicker, setShowDatePicker] = useState(false);
    
    // Lead filter state
    const [leadStatusFilter, setLeadStatusFilter] = useState("all");
    
    const navigate = useNavigate();
    const adminPassword = Cookies.get("admin_auth");

    const getAuthConfig = () => ({
        auth: { username: "admin", password: adminPassword }
    });

    // Fetch products list
    useEffect(() => {
        if (!adminPassword) {
            navigate("/admin");
            return;
        }
        fetchProducts();
    }, [adminPassword, navigate]);

    // Set selected product from URL or first product
    useEffect(() => {
        if (products.length > 0) {
            if (urlProductTag) {
                const product = products.find(p => p.tag === urlProductTag);
                if (product) {
                    setSelectedProduct(product);
                } else {
                    setSelectedProduct(products[0]);
                    navigate(`/admin/dashboard/${products[0].tag}`);
                }
            } else {
                setSelectedProduct(products[0]);
                navigate(`/admin/dashboard/${products[0].tag}`);
            }
        }
    }, [products, urlProductTag, navigate]);

    // Fetch product-specific data when product changes
    useEffect(() => {
        if (selectedProduct) {
            fetchProductData();
        }
    }, [selectedProduct, dateRange]);

    // Fetch global defaults when switching to defaults tab
    useEffect(() => {
        if (activeTab === "defaults" && !globalDefaults) {
            fetchGlobalDefaults();
        }
    }, [activeTab, globalDefaults]);

    const fetchProducts = async () => {
        try {
            const res = await axios.get(`${API}/admin/products`, getAuthConfig());
            setProducts(res.data);
            setIsLoading(false);
        } catch (e) {
            toast.error("Failed to load products");
            if (e.response?.status === 401) {
                Cookies.remove("admin_auth");
                navigate("/admin");
            }
            setIsLoading(false);
        }
    };

    const fetchProductData = async () => {
        if (!selectedProduct) return;
        
        setIsLoading(true);
        try {
            let dateParams = "";
            if (dateRange.from) {
                // Extract year, month, day and create UTC date strings
                // This ensures the date you select is the date that gets filtered
                const fromYear = dateRange.from.getFullYear();
                const fromMonth = String(dateRange.from.getMonth() + 1).padStart(2, '0');
                const fromDay = String(dateRange.from.getDate()).padStart(2, '0');
                const startDateStr = `${fromYear}-${fromMonth}-${fromDay}T00:00:00.000Z`;
                
                const endDate = dateRange.to || dateRange.from;
                const toYear = endDate.getFullYear();
                const toMonth = String(endDate.getMonth() + 1).padStart(2, '0');
                const toDay = String(endDate.getDate()).padStart(2, '0');
                const endDateStr = `${toYear}-${toMonth}-${toDay}T23:59:59.999Z`;
                
                dateParams = `?start_date=${startDateStr}&end_date=${endDateStr}`;
            }
            
            const tag = selectedProduct.tag;
            
            // Build lead filter params
            const leadParams = leadStatusFilter !== "all" ? `?status=${leadStatusFilter}` : "";
            
            const [statsRes, productRes, couponsRes, leadsRes, funnelRes, testimonialsRes, bumpStatsRes] = await Promise.all([
                axios.get(`${API}/admin/products/${tag}/stats${dateParams}`, getAuthConfig()),
                axios.get(`${API}/admin/products/${tag}`, getAuthConfig()),
                axios.get(`${API}/admin/products/${tag}/coupons`, getAuthConfig()),
                axios.get(`${API}/admin/products/${tag}/leads/all${leadParams}`, getAuthConfig()),
                axios.get(`${API}/admin/products/${tag}/funnel${dateParams}`, getAuthConfig()),
                axios.get(`${API}/admin/products/${tag}/testimonials`, getAuthConfig()),
                axios.get(`${API}/admin/products/${tag}/bump-stats${dateParams}`, getAuthConfig())
            ]);
            
            setStats(statsRes.data);
            setProductSettings(productRes.data);
            setCoupons(couponsRes.data);
            setLeads(leadsRes.data);
            setFunnel(funnelRes.data);
            setTestimonials(testimonialsRes.data);
            setEditingSections(productRes.data.sections || {});
            setBumpStats(bumpStatsRes.data);
        } catch (e) {
            toast.error("Failed to load product data");
        } finally {
            setIsLoading(false);
        }
    };
    
    // Separate function to refresh just leads
    const fetchLeads = async (tag, status = null) => {
        if (!tag) return;
        try {
            const statusParam = status && status !== "all" ? `?status=${status}` : "";
            const res = await axios.get(`${API}/admin/products/${tag}/leads/all${statusParam}`, getAuthConfig());
            setLeads(res.data);
        } catch (e) {
            console.error("Failed to refresh leads:", e);
        }
    };

    const handleCreateProduct = async () => {
        if (!newProduct.tag.trim() || !newProduct.name.trim()) {
            toast.error("Please fill in tag and name");
            return;
        }
        try {
            await axios.post(`${API}/admin/products`, newProduct, getAuthConfig());
            toast.success("Product created");
            setShowNewProductDialog(false);
            setNewProduct({ tag: "", name: "", base_price: 47, discount_price: 27, pdf_url: "" });
            fetchProducts();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to create product");
        }
    };

    const handleUpdateProduct = async (updates) => {
        if (!selectedProduct) return;
        try {
            const res = await axios.put(`${API}/admin/products/${selectedProduct.tag}`, updates, getAuthConfig());
            setProductSettings(res.data);
            toast.success("Product updated");
        } catch (e) {
            toast.error("Failed to update product");
        }
    };

    const handleDeleteProduct = async (tag) => {
        if (!window.confirm(`Delete product "${tag}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/admin/products/${tag}`, getAuthConfig());
            toast.success("Product deleted");
            fetchProducts();
            if (selectedProduct?.tag === tag) {
                setSelectedProduct(null);
            }
        } catch (e) {
            toast.error("Failed to delete product");
        }
    };

    const handleCloneProduct = async () => {
        if (!cloneNewTag.trim()) {
            toast.error("Please enter a tag for the cloned product");
            return;
        }
        try {
            await axios.post(`${API}/admin/products/${selectedProduct.tag}/clone?new_tag=${cloneNewTag}`, {}, getAuthConfig());
            toast.success("Product cloned successfully");
            setShowCloneDialog(false);
            setCloneNewTag("");
            fetchProducts();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to clone product");
        }
    };

    const fetchGlobalDefaults = async () => {
        try {
            const res = await axios.get(`${API}/admin/defaults`, getAuthConfig());
            setGlobalDefaults(res.data);
            setEditingDefaults(res.data.sections || {});
        } catch (e) {
            toast.error("Failed to load global defaults");
        }
    };

    const handleUpdateGlobalDefaults = async (sectionName, sectionData) => {
        try {
            const updatedSections = { ...editingDefaults, [sectionName]: sectionData };
            const res = await axios.put(`${API}/admin/defaults`, { sections: updatedSections }, getAuthConfig());
            setGlobalDefaults(res.data);
            setEditingDefaults(res.data.sections || {});
            toast.success(`Default ${sectionName} section updated`);
        } catch (e) {
            toast.error(`Failed to update default ${sectionName} section`);
        }
    };

    const handleUpdateSection = async (sectionName, sectionData) => {
        if (!selectedProduct) return;
        try {
            const res = await axios.put(
                `${API}/admin/products/${selectedProduct.tag}/section/${sectionName}`, 
                sectionData, 
                getAuthConfig()
            );
            setProductSettings(res.data);
            setEditingSections(res.data.sections || {});
            toast.success(`${sectionName} section updated`);
        } catch (e) {
            toast.error(`Failed to update ${sectionName} section`);
        }
    };

    const handleResetSection = async (sectionName) => {
        if (!selectedProduct) return;
        if (!window.confirm(`Reset ${sectionName} section to default? This will override your customizations.`)) return;
        try {
            const res = await axios.post(
                `${API}/admin/products/${selectedProduct.tag}/section/${sectionName}/reset`, 
                {}, 
                getAuthConfig()
            );
            setProductSettings(res.data);
            setEditingSections(res.data.sections || {});
            toast.success(`${sectionName} section reset to default`);
        } catch (e) {
            toast.error(`Failed to reset ${sectionName} section`);
        }
    };

    const handleCreateCoupon = async () => {
        if (!newCoupon.code.trim() || !selectedProduct) {
            toast.error("Please enter a coupon code");
            return;
        }
        try {
            await axios.post(`${API}/admin/products/${selectedProduct.tag}/coupons`, newCoupon, getAuthConfig());
            setNewCoupon({ code: "", discount_percent: 10, max_uses: null });
            fetchProductData();
            toast.success("Coupon created");
        } catch (e) {
            toast.error("Failed to create coupon");
        }
    };

    const handleDeleteCoupon = async (id) => {
        if (!selectedProduct) return;
        try {
            await axios.delete(`${API}/admin/products/${selectedProduct.tag}/coupons/${id}`, getAuthConfig());
            fetchProductData();
            toast.success("Coupon deleted");
        } catch (e) {
            toast.error("Failed to delete coupon");
        }
    };

    const handleToggleCoupon = async (id) => {
        if (!selectedProduct) return;
        try {
            await axios.put(`${API}/admin/products/${selectedProduct.tag}/coupons/${id}/toggle`, {}, getAuthConfig());
            fetchProductData();
        } catch (e) {
            toast.error("Failed to toggle coupon");
        }
    };

    const handleExportCustomers = async () => {
        if (!selectedProduct) return;
        try {
            const res = await axios.get(`${API}/admin/products/${selectedProduct.tag}/customers/export`, {
                ...getAuthConfig(),
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${selectedProduct.tag}_customers.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("Export downloaded");
        } catch (e) {
            toast.error("Failed to export customers");
        }
    };

    const handleExportLeads = async () => {
        if (!selectedProduct) return;
        try {
            const res = await axios.get(`${API}/admin/products/${selectedProduct.tag}/leads/export`, {
                ...getAuthConfig(),
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${selectedProduct.tag}_leads.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("Export downloaded");
        } catch (e) {
            toast.error("Failed to export leads");
        }
    };

    const handleCreateTestimonial = async () => {
        if (!newTestimonial.name.trim() || !newTestimonial.text.trim() || !selectedProduct) {
            toast.error("Please fill in name and text");
            return;
        }
        try {
            await axios.post(`${API}/admin/products/${selectedProduct.tag}/testimonials`, newTestimonial, getAuthConfig());
            setNewTestimonial({ name: "", age: 30, text: "", rating: 5 });
            fetchProductData();
            toast.success("Testimonial created");
        } catch (e) {
            toast.error("Failed to create testimonial");
        }
    };

    const handleDeleteTestimonial = async (id) => {
        if (!selectedProduct) return;
        try {
            await axios.delete(`${API}/admin/products/${selectedProduct.tag}/testimonials/${id}`, getAuthConfig());
            fetchProductData();
            toast.success("Testimonial deleted");
        } catch (e) {
            toast.error("Failed to delete testimonial");
        }
    };

    const handleLogout = () => {
        Cookies.remove("admin_auth");
        navigate("/admin");
    };

    // Section field update helper
    const updateSectionField = (sectionName, field, value) => {
        setEditingSections(prev => ({
            ...prev,
            [sectionName]: {
                ...prev[sectionName],
                [field]: value
            }
        }));
    };

    // Update nested array item in section
    const updateSectionArrayItem = (sectionName, arrayField, index, itemField, value) => {
        setEditingSections(prev => {
            const newArray = [...(prev[sectionName]?.[arrayField] || [])];
            newArray[index] = { ...newArray[index], [itemField]: value };
            return {
                ...prev,
                [sectionName]: {
                    ...prev[sectionName],
                    [arrayField]: newArray
                }
            };
        });
    };

    // Add item to section array
    const addSectionArrayItem = (sectionName, arrayField, newItem) => {
        setEditingSections(prev => ({
            ...prev,
            [sectionName]: {
                ...prev[sectionName],
                [arrayField]: [...(prev[sectionName]?.[arrayField] || []), newItem]
            }
        }));
    };

    // Remove item from section array
    const removeSectionArrayItem = (sectionName, arrayField, index) => {
        setEditingSections(prev => ({
            ...prev,
            [sectionName]: {
                ...prev[sectionName],
                [arrayField]: (prev[sectionName]?.[arrayField] || []).filter((_, i) => i !== index)
            }
        }));
    };

    // Export sections as text file
    const handleExportSections = async () => {
        try {
            const res = await axios.get(
                `${API}/admin/products/${selectedProduct.tag}/export-sections`,
                { ...getAuthConfig(), responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `${selectedProduct.tag}_sections.txt`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success("Sections exported");
        } catch { toast.error("Export failed"); }
    };

    // Import sections from text file
    const handleImportSections = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const formData = new FormData();
        formData.append("file", file);
        try {
            const res = await axios.post(
                `${API}/admin/products/${selectedProduct.tag}/import-sections`,
                formData,
                { ...getAuthConfig(), headers: { "Content-Type": "multipart/form-data" } }
            );
            setEditingSections(res.data.sections || {});
            setProductSettings(prev => ({ ...prev, customized_sections: res.data.customized_sections || [] }));
            toast.success("Sections updated from file");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Import failed");
        }
        e.target.value = "";
    };

    if (isLoading && products.length === 0) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const tabs = [
        { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
        { id: "sections", label: "Sections", icon: FileText },
        { id: "defaults", label: "Defaults", icon: Settings },
        { id: "settings", label: "Settings", icon: Settings },
        { id: "coupons", label: "Coupons", icon: Tag },
        { id: "leads", label: "Leads", icon: Users },
        { id: "testimonials", label: "Testimonials", icon: Users },
        { id: "funnel", label: "Funnel", icon: BarChart2 }
    ];

    const isCustomized = (sectionName) => {
        return productSettings?.customized_sections?.includes(sectionName);
    };

    return (
        <div className="min-h-screen bg-background flex">
            {/* Sidebar */}
            <aside className="w-64 bg-card border-r border-border p-6 flex flex-col">
                <div className="mb-6">
                    <h1 className="font-heading text-xl font-bold gold-text">HSI Admin</h1>
                    <p className="text-sm text-muted-foreground">Control Center</p>
                </div>

                {/* Product Selector */}
                <div className="mb-6">
                    <Label className="text-xs text-muted-foreground uppercase mb-2 block">Product</Label>
                    <Popover open={showProductSelector} onOpenChange={setShowProductSelector}>
                        <PopoverTrigger asChild>
                            <Button 
                                className="w-full justify-between bg-secondary border border-border text-left"
                                data-testid="product-selector"
                            >
                                <span className="truncate">{selectedProduct?.name || "Select product"}</span>
                                <ChevronDown className="w-4 h-4 ml-2" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-2 bg-card border-border">
                            {products.map((product) => (
                                <button
                                    key={product.tag}
                                    onClick={() => {
                                        setSelectedProduct(product);
                                        navigate(`/admin/dashboard/${product.tag}`);
                                        setShowProductSelector(false);
                                    }}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary transition-colors ${
                                        selectedProduct?.tag === product.tag ? "bg-primary/10 text-primary" : ""
                                    }`}
                                >
                                    {product.name}
                                </button>
                            ))}
                            <Dialog open={showNewProductDialog} onOpenChange={setShowNewProductDialog}>
                                <DialogTrigger asChild>
                                    <button className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-secondary transition-colors flex items-center gap-2">
                                        <Plus className="w-4 h-4" />
                                        New Product
                                    </button>
                                </DialogTrigger>
                                <DialogContent className="bg-card border-border">
                                    <DialogHeader>
                                        <DialogTitle className="font-heading">Create New Product</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4 pt-4">
                                        <div>
                                            <Label className="text-sm text-muted-foreground">URL Tag</Label>
                                            <Input
                                                placeholder="ebook-health"
                                                value={newProduct.tag}
                                                onChange={(e) => setNewProduct({ ...newProduct, tag: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                                                className="mt-1 bg-input border-border"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-sm text-muted-foreground">Product Name</Label>
                                            <Input
                                                placeholder="The Health Guide"
                                                value={newProduct.name}
                                                onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                                                className="mt-1 bg-input border-border"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Base Price ($)</Label>
                                                <Input
                                                    type="number"
                                                    value={newProduct.base_price}
                                                    onChange={(e) => setNewProduct({ ...newProduct, base_price: parseFloat(e.target.value) })}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Discount Price ($)</Label>
                                                <Input
                                                    type="number"
                                                    value={newProduct.discount_price}
                                                    onChange={(e) => setNewProduct({ ...newProduct, discount_price: parseFloat(e.target.value) })}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <Label className="text-sm text-muted-foreground">Product PDF</Label>
                                            <PdfUploadField 
                                                value={newProduct.pdf_url}
                                                onChange={(url) => setNewProduct({ ...newProduct, pdf_url: url })}
                                                authConfig={getAuthConfig()}
                                            />
                                        </div>
                                        <Button onClick={handleCreateProduct} className="btn-primary w-full">
                                            Create Product
                                        </Button>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </PopoverContent>
                    </Popover>
                    {selectedProduct && (
                        <a 
                            href={`/${selectedProduct.tag}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline mt-2 flex items-center gap-1"
                        >
                            View landing page <ExternalLink className="w-3 h-3" />
                        </a>
                    )}
                </div>

                <nav className="flex-1 space-y-1">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                                activeTab === tab.id 
                                    ? "bg-primary/10 text-primary border-l-2 border-primary" 
                                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                            }`}
                            data-testid={`tab-${tab.id}`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </nav>

                <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground hover:text-destructive transition-colors"
                    data-testid="logout-button"
                >
                    <LogOut className="w-5 h-5" />
                    Logout
                </button>
            </aside>

            {/* Main Content */}
            <main className="flex-1 p-8 overflow-auto">
                {!selectedProduct ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                            <h2 className="font-heading text-2xl font-bold mb-2">No Products Yet</h2>
                            <p className="text-muted-foreground mb-4">Create your first product to get started</p>
                            <Button onClick={() => setShowNewProductDialog(true)} className="btn-primary">
                                <Plus className="w-4 h-4 mr-2" />
                                Create Product
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="max-w-6xl mx-auto">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h2 className="font-heading text-2xl font-bold">
                                    {tabs.find(t => t.id === activeTab)?.label}
                                </h2>
                                <p className="text-sm text-muted-foreground">{selectedProduct.name}</p>
                            </div>
                            <div className="flex items-center gap-4">
                                {(activeTab === "dashboard" || activeTab === "funnel") && (
                                    <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                                        <PopoverTrigger asChild>
                                            <Button className="btn-secondary" data-testid="date-filter-button">
                                                <Calendar className="w-4 h-4 mr-2" />
                                                {dateRange.from ? (
                                                    dateRange.to ? (
                                                        `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d")}`
                                                    ) : (
                                                        format(dateRange.from, "MMM d, yyyy")
                                                    )
                                                ) : (
                                                    "Filter by Date"
                                                )}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0 bg-card border-border" align="end">
                                            <div className="p-4 space-y-4">
                                                <CalendarComponent
                                                    mode="range"
                                                    selected={dateRange}
                                                    onSelect={(range) => setDateRange(range || { from: null, to: null })}
                                                    numberOfMonths={2}
                                                    className="bg-card"
                                                />
                                                <div className="flex justify-between">
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm"
                                                        onClick={() => {
                                                            setDateRange({ from: null, to: null });
                                                            setShowDatePicker(false);
                                                        }}
                                                    >
                                                        Clear
                                                    </Button>
                                                    <Button 
                                                        size="sm"
                                                        className="btn-primary"
                                                        onClick={() => setShowDatePicker(false)}
                                                    >
                                                        Apply
                                                    </Button>
                                                </div>
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                )}
                                <Button onClick={fetchProductData} className="btn-secondary">
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Refresh
                                </Button>
                            </div>
                        </div>

                        {/* Dashboard Tab */}
                        {activeTab === "dashboard" && stats && (
                            <div className="space-y-8">
                                {/* Reset Stats Button */}
                                <div className="flex justify-end">
                                    <ResetStatsButton 
                                        productTag={selectedProduct?.tag}
                                        authConfig={getAuthConfig()}
                                        onReset={() => fetchProductData(selectedProduct?.tag)}
                                    />
                                </div>

                                <div className="grid grid-cols-4 gap-6">
                                    {[
                                        { label: "Total Revenue", value: `$${stats.total_revenue}`, icon: DollarSign, color: "text-primary" },
                                        { label: "Total Sales", value: stats.total_sales, icon: CheckCircle, color: "text-green-500" },
                                        { label: "Unique Visitors", value: stats.unique_visitors, icon: Eye, color: "text-blue-500" },
                                        { label: "Conversion Rate", value: `${stats.conversion_rate}%`, icon: TrendingUp, color: "text-accent" }
                                    ].map((stat, i) => (
                                        <motion.div
                                            key={i}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: i * 0.1 }}
                                            className="bg-card border border-border p-6"
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm text-muted-foreground">{stat.label}</span>
                                                <stat.icon className={`w-5 h-5 ${stat.color}`} />
                                            </div>
                                            <p className="text-3xl font-mono font-bold">{stat.value}</p>
                                        </motion.div>
                                    ))}
                                </div>

                                <div className="grid grid-cols-3 gap-6">
                                    <div className="bg-card border border-border p-6">
                                        <p className="text-sm text-muted-foreground mb-1">Today's Revenue</p>
                                        <p className="text-2xl font-mono font-bold text-primary">${stats.today_revenue}</p>
                                    </div>
                                    <div className="bg-card border border-border p-6">
                                        <p className="text-sm text-muted-foreground mb-1">Today's Sales</p>
                                        <p className="text-2xl font-mono font-bold">{stats.today_sales}</p>
                                    </div>
                                    <div className="bg-card border border-border p-6">
                                        <p className="text-sm text-muted-foreground mb-1">Today's Views</p>
                                        <p className="text-2xl font-mono font-bold">{stats.today_views}</p>
                                    </div>
                                </div>

                                <div className="bg-card border border-border p-6">
                                    <h3 className="font-heading text-lg font-bold mb-6">Sales Trend</h3>
                                    <div className="h-64">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={stats.sales_by_day}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                                <XAxis dataKey="date" stroke="#666" fontSize={12} />
                                                <YAxis stroke="#666" fontSize={12} />
                                                <Tooltip 
                                                    contentStyle={{ backgroundColor: '#0A0A0A', border: '1px solid #333', borderRadius: 0 }} 
                                                />
                                                <Area type="monotone" dataKey="sales" stroke="#D4AF37" fill="rgba(212, 175, 55, 0.2)" />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                {/* Order Bump Analytics */}
                                {bumpStats && productSettings?.order_bump_enabled && (
                                    <div className="bg-card border border-primary/30 p-6">
                                        <div className="flex items-center justify-between mb-6">
                                            <h3 className="font-heading text-lg font-bold">Order Bump Analytics</h3>
                                            <span className="text-xs bg-primary/20 text-primary px-2 py-1">UPSELL PERFORMANCE</span>
                                        </div>
                                        <div className="grid grid-cols-4 gap-4 mb-6">
                                            <div className="bg-secondary/30 p-4">
                                                <p className="text-xs text-muted-foreground uppercase mb-1">Shown To</p>
                                                <p className="text-2xl font-mono font-bold">{bumpStats.bump_shown}</p>
                                                <p className="text-xs text-muted-foreground">visitors</p>
                                            </div>
                                            <div className="bg-secondary/30 p-4">
                                                <p className="text-xs text-muted-foreground uppercase mb-1">Selected</p>
                                                <p className="text-2xl font-mono font-bold text-accent">{bumpStats.bump_selected}</p>
                                                <p className="text-xs text-muted-foreground">{bumpStats.selection_rate}% selection rate</p>
                                            </div>
                                            <div className="bg-secondary/30 p-4">
                                                <p className="text-xs text-muted-foreground uppercase mb-1">Purchased</p>
                                                <p className="text-2xl font-mono font-bold text-primary">{bumpStats.bump_purchased}</p>
                                                <p className="text-xs text-muted-foreground">{bumpStats.purchase_rate}% of checkouts</p>
                                            </div>
                                            <div className="bg-secondary/30 p-4">
                                                <p className="text-xs text-muted-foreground uppercase mb-1">Bump Revenue</p>
                                                <p className="text-2xl font-mono font-bold gold-text">${bumpStats.bump_revenue}</p>
                                                <p className="text-xs text-muted-foreground">extra revenue</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-6 text-sm text-muted-foreground">
                                            <div className="flex items-center gap-2">
                                                <span className="w-2 h-2 bg-accent rounded-full"></span>
                                                <span>Selection Rate: <strong className="text-accent">{bumpStats.selection_rate}%</strong></span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="w-2 h-2 bg-primary rounded-full"></span>
                                                <span>Purchase Rate: <strong className="text-primary">{bumpStats.purchase_rate}%</strong></span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="w-2 h-2 bg-muted-foreground rounded-full"></span>
                                                <span>Deselected: {bumpStats.bump_deselected} times</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Sections Tab */}
                        {activeTab === "sections" && editingSections && (
                            <div className="space-y-4">
                                <div className="bg-secondary/30 border border-border p-4 mb-6 flex items-center justify-between gap-4">
                                    <p className="text-sm text-muted-foreground">
                                        Customize each section of your landing page. Changes marked as "Customized" override the global defaults.
                                        Use "Reset to Default" to revert to global settings.
                                    </p>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <Button variant="outline" size="sm" onClick={handleExportSections} data-testid="export-sections-btn">
                                            <Download className="w-4 h-4 mr-1.5" /> Export
                                        </Button>
                                        <label>
                                            <input type="file" accept=".txt" onChange={handleImportSections} className="hidden" data-testid="import-sections-input" />
                                            <Button variant="outline" size="sm" asChild>
                                                <span data-testid="import-sections-btn"><Upload className="w-4 h-4 mr-1.5" /> Import</span>
                                            </Button>
                                        </label>
                                    </div>
                                </div>

                                <Accordion type="single" collapsible className="space-y-2">
                                    {/* Hero Section */}
                                    <SectionEditor 
                                        sectionName="hero" 
                                        title="Hero Section"
                                        isCustomized={isCustomized('hero')}
                                        onSave={() => handleUpdateSection('hero', editingSections.hero)}
                                        onReset={() => handleResetSection('hero')}
                                    >
                                        <div className="grid gap-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Headline</Label>
                                                <Input
                                                    value={editingSections.hero?.headline || ""}
                                                    onChange={(e) => updateSectionField('hero', 'headline', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Subheadline</Label>
                                                <Textarea
                                                    value={editingSections.hero?.subheadline || ""}
                                                    onChange={(e) => updateSectionField('hero', 'subheadline', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Hero Image URL</Label>
                                                <Input
                                                    value={editingSections.hero?.hero_image_url || ""}
                                                    onChange={(e) => updateSectionField('hero', 'hero_image_url', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div className="grid grid-cols-3 gap-4">
                                                <div>
                                                    <Label className="text-sm text-muted-foreground">Badge 1 Text</Label>
                                                    <Input
                                                        value={editingSections.hero?.badge1_text || ""}
                                                        onChange={(e) => updateSectionField('hero', 'badge1_text', e.target.value)}
                                                        className="mt-1 bg-input border-border"
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="text-sm text-muted-foreground">Badge 2 Text</Label>
                                                    <Input
                                                        value={editingSections.hero?.badge2_text || ""}
                                                        onChange={(e) => updateSectionField('hero', 'badge2_text', e.target.value)}
                                                        className="mt-1 bg-input border-border"
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="text-sm text-muted-foreground">Badge 3 Text</Label>
                                                    <Input
                                                        value={editingSections.hero?.badge3_text || ""}
                                                        onChange={(e) => updateSectionField('hero', 'badge3_text', e.target.value)}
                                                        className="mt-1 bg-input border-border"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">CTA Button Text</Label>
                                                <Input
                                                    value={editingSections.hero?.cta_text || ""}
                                                    onChange={(e) => updateSectionField('hero', 'cta_text', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                        </div>
                                    </SectionEditor>

                                    {/* Authority Section */}
                                    <SectionEditor 
                                        sectionName="authority" 
                                        title="Authority Section"
                                        isCustomized={isCustomized('authority')}
                                        onSave={() => handleUpdateSection('authority', editingSections.authority)}
                                        onReset={() => handleResetSection('authority')}
                                    >
                                        <div className="grid gap-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Pre-title</Label>
                                                <Input
                                                    value={editingSections.authority?.pretitle || ""}
                                                    onChange={(e) => updateSectionField('authority', 'pretitle', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Title</Label>
                                                <Input
                                                    value={editingSections.authority?.title || ""}
                                                    onChange={(e) => updateSectionField('authority', 'title', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Description</Label>
                                                <Textarea
                                                    value={editingSections.authority?.description || ""}
                                                    onChange={(e) => updateSectionField('authority', 'description', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground mb-2 block">Authority Cards</Label>
                                                {(editingSections.authority?.cards || []).map((card, idx) => (
                                                    <div key={idx} className="grid grid-cols-4 gap-2 mb-2 p-3 bg-secondary/30 border border-border">
                                                        <Input
                                                            placeholder="Icon (Award, Users, BookOpen)"
                                                            value={card.icon || ""}
                                                            onChange={(e) => updateSectionArrayItem('authority', 'cards', idx, 'icon', e.target.value)}
                                                            className="bg-input border-border"
                                                        />
                                                        <Input
                                                            placeholder="Title"
                                                            value={card.title || ""}
                                                            onChange={(e) => updateSectionArrayItem('authority', 'cards', idx, 'title', e.target.value)}
                                                            className="bg-input border-border"
                                                        />
                                                        <Input
                                                            placeholder="Description"
                                                            value={card.description || ""}
                                                            onChange={(e) => updateSectionArrayItem('authority', 'cards', idx, 'description', e.target.value)}
                                                            className="bg-input border-border"
                                                        />
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => removeSectionArrayItem('authority', 'cards', idx)}
                                                            className="text-destructive"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                ))}
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => addSectionArrayItem('authority', 'cards', { icon: "Award", title: "", description: "" })}
                                                    className="mt-2"
                                                >
                                                    <Plus className="w-4 h-4 mr-2" /> Add Card
                                                </Button>
                                            </div>
                                        </div>
                                    </SectionEditor>

                                    {/* Features Section */}
                                    <SectionEditor 
                                        sectionName="features" 
                                        title="Features Section"
                                        isCustomized={isCustomized('features')}
                                        onSave={() => handleUpdateSection('features', editingSections.features)}
                                        onReset={() => handleResetSection('features')}
                                    >
                                        <div className="grid gap-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Pre-title</Label>
                                                <Input
                                                    value={editingSections.features?.pretitle || ""}
                                                    onChange={(e) => updateSectionField('features', 'pretitle', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Title</Label>
                                                <Input
                                                    value={editingSections.features?.title || ""}
                                                    onChange={(e) => updateSectionField('features', 'title', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground mb-2 block">Features</Label>
                                                {(editingSections.features?.features || []).map((feature, idx) => (
                                                    <div key={idx} className="grid grid-cols-4 gap-2 mb-2 p-3 bg-secondary/30 border border-border">
                                                        <Input
                                                            placeholder="Icon"
                                                            value={feature.icon || ""}
                                                            onChange={(e) => updateSectionArrayItem('features', 'features', idx, 'icon', e.target.value)}
                                                            className="bg-input border-border"
                                                        />
                                                        <Input
                                                            placeholder="Title"
                                                            value={feature.title || ""}
                                                            onChange={(e) => updateSectionArrayItem('features', 'features', idx, 'title', e.target.value)}
                                                            className="bg-input border-border"
                                                        />
                                                        <Input
                                                            placeholder="Description"
                                                            value={feature.description || ""}
                                                            onChange={(e) => updateSectionArrayItem('features', 'features', idx, 'description', e.target.value)}
                                                            className="bg-input border-border"
                                                        />
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => removeSectionArrayItem('features', 'features', idx)}
                                                            className="text-destructive"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                ))}
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => addSectionArrayItem('features', 'features', { icon: "CheckCircle", title: "", description: "" })}
                                                    className="mt-2"
                                                >
                                                    <Plus className="w-4 h-4 mr-2" /> Add Feature
                                                </Button>
                                            </div>
                                        </div>
                                    </SectionEditor>

                                    {/* Testimonials Section */}
                                    <SectionEditor 
                                        sectionName="testimonials" 
                                        title="Testimonials Section"
                                        isCustomized={isCustomized('testimonials')}
                                        onSave={() => handleUpdateSection('testimonials', editingSections.testimonials)}
                                        onReset={() => handleResetSection('testimonials')}
                                    >
                                        <div className="grid gap-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Pre-title</Label>
                                                <Input
                                                    value={editingSections.testimonials?.pretitle || ""}
                                                    onChange={(e) => updateSectionField('testimonials', 'pretitle', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Title</Label>
                                                <Input
                                                    value={editingSections.testimonials?.title || ""}
                                                    onChange={(e) => updateSectionField('testimonials', 'title', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                Note: Testimonial content is managed in the "Testimonials" tab. This section only controls the section header.
                                            </p>
                                        </div>
                                    </SectionEditor>

                                    {/* FAQ Section */}
                                    <SectionEditor 
                                        sectionName="faq" 
                                        title="FAQ Section"
                                        isCustomized={isCustomized('faq')}
                                        onSave={() => handleUpdateSection('faq', editingSections.faq)}
                                        onReset={() => handleResetSection('faq')}
                                    >
                                        <div className="grid gap-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Pre-title</Label>
                                                <Input
                                                    value={editingSections.faq?.pretitle || ""}
                                                    onChange={(e) => updateSectionField('faq', 'pretitle', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Title</Label>
                                                <Input
                                                    value={editingSections.faq?.title || ""}
                                                    onChange={(e) => updateSectionField('faq', 'title', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground mb-2 block">FAQ Items</Label>
                                                {(editingSections.faq?.items || []).map((item, idx) => (
                                                    <div key={idx} className="mb-3 p-3 bg-secondary/30 border border-border">
                                                        <div className="flex justify-between items-start mb-2">
                                                            <Label className="text-xs text-muted-foreground">Question {idx + 1}</Label>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => removeSectionArrayItem('faq', 'items', idx)}
                                                                className="text-destructive h-6 w-6 p-0"
                                                            >
                                                                <Trash2 className="w-3 h-3" />
                                                            </Button>
                                                        </div>
                                                        <Input
                                                            placeholder="Question"
                                                            value={item.question || ""}
                                                            onChange={(e) => updateSectionArrayItem('faq', 'items', idx, 'question', e.target.value)}
                                                            className="mb-2 bg-input border-border"
                                                        />
                                                        <Textarea
                                                            placeholder="Answer"
                                                            value={item.answer || ""}
                                                            onChange={(e) => updateSectionArrayItem('faq', 'items', idx, 'answer', e.target.value)}
                                                            className="bg-input border-border"
                                                            rows={2}
                                                        />
                                                    </div>
                                                ))}
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => addSectionArrayItem('faq', 'items', { question: "", answer: "" })}
                                                    className="mt-2"
                                                >
                                                    <Plus className="w-4 h-4 mr-2" /> Add FAQ
                                                </Button>
                                            </div>
                                        </div>
                                    </SectionEditor>

                                    {/* Pricing Section */}
                                    <SectionEditor 
                                        sectionName="pricing" 
                                        title="Pricing Section"
                                        isCustomized={isCustomized('pricing')}
                                        onSave={() => handleUpdateSection('pricing', editingSections.pricing)}
                                        onReset={() => handleResetSection('pricing')}
                                    >
                                        <div className="grid gap-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Pre-title</Label>
                                                <Input
                                                    value={editingSections.pricing?.pretitle || ""}
                                                    onChange={(e) => updateSectionField('pricing', 'pretitle', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Title</Label>
                                                <Input
                                                    value={editingSections.pricing?.title || ""}
                                                    onChange={(e) => updateSectionField('pricing', 'title', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground mb-2 block">Features List</Label>
                                                {(editingSections.pricing?.features || []).map((feature, idx) => (
                                                    <div key={idx} className="flex gap-2 mb-2">
                                                        <Input
                                                            placeholder="Feature text"
                                                            value={feature.text || ""}
                                                            onChange={(e) => updateSectionArrayItem('pricing', 'features', idx, 'text', e.target.value)}
                                                            className="bg-input border-border"
                                                        />
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => removeSectionArrayItem('pricing', 'features', idx)}
                                                            className="text-destructive"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                ))}
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => addSectionArrayItem('pricing', 'features', { text: "" })}
                                                    className="mt-2"
                                                >
                                                    <Plus className="w-4 h-4 mr-2" /> Add Feature
                                                </Button>
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">CTA Button Text</Label>
                                                <Input
                                                    value={editingSections.pricing?.cta_text || ""}
                                                    onChange={(e) => updateSectionField('pricing', 'cta_text', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Guarantee Title</Label>
                                                <Input
                                                    value={editingSections.pricing?.guarantee_title || ""}
                                                    onChange={(e) => updateSectionField('pricing', 'guarantee_title', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Guarantee Text</Label>
                                                <Input
                                                    value={editingSections.pricing?.guarantee_text || ""}
                                                    onChange={(e) => updateSectionField('pricing', 'guarantee_text', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                        </div>
                                    </SectionEditor>

                                    {/* Footer Section */}
                                    <SectionEditor 
                                        sectionName="footer" 
                                        title="Footer Section"
                                        isCustomized={isCustomized('footer')}
                                        onSave={() => handleUpdateSection('footer', editingSections.footer)}
                                        onReset={() => handleResetSection('footer')}
                                    >
                                        <div className="grid gap-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Copyright Text</Label>
                                                <Input
                                                    value={editingSections.footer?.copyright_text || ""}
                                                    onChange={(e) => updateSectionField('footer', 'copyright_text', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Disclaimer</Label>
                                                <Textarea
                                                    value={editingSections.footer?.disclaimer || ""}
                                                    onChange={(e) => updateSectionField('footer', 'disclaimer', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                        </div>
                                    </SectionEditor>

                                    {/* Exit Popup Section */}
                                    <SectionEditor 
                                        sectionName="exit_popup" 
                                        title="Exit Popup"
                                        isCustomized={isCustomized('exit_popup')}
                                        onSave={() => handleUpdateSection('exit_popup', editingSections.exit_popup)}
                                        onReset={() => handleResetSection('exit_popup')}
                                    >
                                        <div className="grid gap-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Title</Label>
                                                <Input
                                                    value={editingSections.exit_popup?.title || ""}
                                                    onChange={(e) => updateSectionField('exit_popup', 'title', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Description</Label>
                                                <Textarea
                                                    value={editingSections.exit_popup?.description || ""}
                                                    onChange={(e) => updateSectionField('exit_popup', 'description', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Button Text</Label>
                                                <Input
                                                    value={editingSections.exit_popup?.button_text || ""}
                                                    onChange={(e) => updateSectionField('exit_popup', 'button_text', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Success Title</Label>
                                                <Input
                                                    value={editingSections.exit_popup?.success_title || ""}
                                                    onChange={(e) => updateSectionField('exit_popup', 'success_title', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Success Text</Label>
                                                <Input
                                                    value={editingSections.exit_popup?.success_text || ""}
                                                    onChange={(e) => updateSectionField('exit_popup', 'success_text', e.target.value)}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Popup Coupon Code</Label>
                                                <select
                                                    value={editingSections.exit_popup?.coupon_code || ""}
                                                    onChange={(e) => updateSectionField('exit_popup', 'coupon_code', e.target.value)}
                                                    className="w-full mt-1 h-10 px-3 bg-input border border-border text-foreground rounded-md"
                                                >
                                                    <option value="">Select a coupon...</option>
                                                    {coupons.filter(c => c.active).map(coupon => (
                                                        <option key={coupon.id} value={coupon.code}>
                                                            {coupon.code} ({coupon.discount_percent}% off)
                                                        </option>
                                                    ))}
                                                </select>
                                                <p className="text-xs text-muted-foreground mt-1">Used in popup and popup emails</p>
                                            </div>
                                        </div>
                                    </SectionEditor>
                                </Accordion>
                            </div>
                        )}

                        {/* Global Defaults Tab */}
                        {activeTab === "defaults" && editingDefaults && (
                            <div className="space-y-4">
                                <div className="bg-secondary/30 border border-border p-4 mb-6">
                                    <p className="text-sm text-muted-foreground">
                                        Configure the <strong>global default values</strong> for all landing page sections. 
                                        New products will use these defaults. Products can override specific sections.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    {/* Default Hero Section */}
                                    <div className="bg-card border border-border p-6">
                                        <h3 className="font-heading font-bold mb-4">Default Hero Section</h3>
                                        <div className="grid gap-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Default Headline</Label>
                                                <Input
                                                    value={editingDefaults.hero?.headline || ""}
                                                    onChange={(e) => setEditingDefaults({...editingDefaults, hero: {...editingDefaults.hero, headline: e.target.value}})}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Default Subheadline</Label>
                                                <Textarea
                                                    value={editingDefaults.hero?.subheadline || ""}
                                                    onChange={(e) => setEditingDefaults({...editingDefaults, hero: {...editingDefaults.hero, subheadline: e.target.value}})}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Default CTA Text</Label>
                                                <Input
                                                    value={editingDefaults.hero?.cta_text || ""}
                                                    onChange={(e) => setEditingDefaults({...editingDefaults, hero: {...editingDefaults.hero, cta_text: e.target.value}})}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <Button 
                                                onClick={() => handleUpdateGlobalDefaults('hero', editingDefaults.hero)}
                                                className="btn-primary w-fit"
                                            >
                                                Save Hero Defaults
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Default Authority Section */}
                                    <div className="bg-card border border-border p-6">
                                        <h3 className="font-heading font-bold mb-4">Default Authority Section</h3>
                                        <div className="grid gap-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Default Pre-title</Label>
                                                <Input
                                                    value={editingDefaults.authority?.pretitle || ""}
                                                    onChange={(e) => setEditingDefaults({...editingDefaults, authority: {...editingDefaults.authority, pretitle: e.target.value}})}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Default Title</Label>
                                                <Input
                                                    value={editingDefaults.authority?.title || ""}
                                                    onChange={(e) => setEditingDefaults({...editingDefaults, authority: {...editingDefaults.authority, title: e.target.value}})}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <Button 
                                                onClick={() => handleUpdateGlobalDefaults('authority', editingDefaults.authority)}
                                                className="btn-primary w-fit"
                                            >
                                                Save Authority Defaults
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Default Features Section */}
                                    <div className="bg-card border border-border p-6">
                                        <h3 className="font-heading font-bold mb-4">Default Features Section</h3>
                                        <div className="grid gap-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Default Pre-title</Label>
                                                <Input
                                                    value={editingDefaults.features?.pretitle || ""}
                                                    onChange={(e) => setEditingDefaults({...editingDefaults, features: {...editingDefaults.features, pretitle: e.target.value}})}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Default Title</Label>
                                                <Input
                                                    value={editingDefaults.features?.title || ""}
                                                    onChange={(e) => setEditingDefaults({...editingDefaults, features: {...editingDefaults.features, title: e.target.value}})}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <Button 
                                                onClick={() => handleUpdateGlobalDefaults('features', editingDefaults.features)}
                                                className="btn-primary w-fit"
                                            >
                                                Save Features Defaults
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Default Pricing Section */}
                                    <div className="bg-card border border-border p-6">
                                        <h3 className="font-heading font-bold mb-4">Default Pricing Section</h3>
                                        <div className="grid gap-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Default CTA Text</Label>
                                                <Input
                                                    value={editingDefaults.pricing?.cta_text || ""}
                                                    onChange={(e) => setEditingDefaults({...editingDefaults, pricing: {...editingDefaults.pricing, cta_text: e.target.value}})}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Default Guarantee Title</Label>
                                                <Input
                                                    value={editingDefaults.pricing?.guarantee_title || ""}
                                                    onChange={(e) => setEditingDefaults({...editingDefaults, pricing: {...editingDefaults.pricing, guarantee_title: e.target.value}})}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <Button 
                                                onClick={() => handleUpdateGlobalDefaults('pricing', editingDefaults.pricing)}
                                                className="btn-primary w-fit"
                                            >
                                                Save Pricing Defaults
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Default Exit Popup Section */}
                                    <div className="bg-card border border-border p-6">
                                        <h3 className="font-heading font-bold mb-4">Default Popup Section</h3>
                                        <div className="grid gap-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Default Title</Label>
                                                <Input
                                                    value={editingDefaults.exit_popup?.title || ""}
                                                    onChange={(e) => setEditingDefaults({...editingDefaults, exit_popup: {...editingDefaults.exit_popup, title: e.target.value}})}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Default Description</Label>
                                                <Textarea
                                                    value={editingDefaults.exit_popup?.description || ""}
                                                    onChange={(e) => setEditingDefaults({...editingDefaults, exit_popup: {...editingDefaults.exit_popup, description: e.target.value}})}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Default Coupon Code</Label>
                                                <Input
                                                    value={editingDefaults.exit_popup?.coupon_code || ""}
                                                    onChange={(e) => setEditingDefaults({...editingDefaults, exit_popup: {...editingDefaults.exit_popup, coupon_code: e.target.value}})}
                                                    className="mt-1 bg-input border-border"
                                                />
                                            </div>
                                            <Button 
                                                onClick={() => handleUpdateGlobalDefaults('exit_popup', editingDefaults.exit_popup)}
                                                className="btn-primary w-fit"
                                            >
                                                Save Popup Defaults
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                {/* SITE SETTINGS SECTION */}
                                <div className="mt-10 pt-8 border-t-2 border-primary/30">
                                    <h2 className="font-heading text-xl font-bold mb-6 flex items-center gap-2">
                                        <Globe className="w-6 h-6 text-primary" />
                                        Impostazioni Sito
                                    </h2>
                                    <div className="space-y-4">
                                        {/* Site URL Settings */}
                                        <SiteUrlSettingsCard authConfig={getAuthConfig()} />
                                        
                                        {/* Download Settings */}
                                        <DownloadSettingsCard authConfig={getAuthConfig()} />
                                    </div>
                                </div>

                                {/* EMAIL TEMPLATES SECTION */}
                                <div className="mt-10 pt-8 border-t-2 border-primary/30">
                                    <h2 className="font-heading text-xl font-bold mb-6 flex items-center gap-2">
                                        <Mail className="w-6 h-6 text-primary" />
                                        Template Email
                                    </h2>
                                    <div className="space-y-4">
                                        {/* Purchase Email Template */}
                                        <EmailSettingsCard authConfig={getAuthConfig()} />
                                        
                                        {/* Popup Email Template */}
                                        <PopupEmailCard authConfig={getAuthConfig()} productSettings={productSettings} />
                                        
                                        {/* Abandoned Cart Email Template */}
                                        <AbandonedCartEmailCard authConfig={getAuthConfig()} productSettings={productSettings} />
                                        
                                        {/* Recall Email Template */}
                                        <RecallEmailCard authConfig={getAuthConfig()} productSettings={productSettings} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Settings Tab */}
                        {activeTab === "settings" && productSettings && (
                            <div className="space-y-8">
                                <div className="bg-card border border-border p-6">
                                    <h3 className="font-heading text-lg font-bold mb-6">Product Settings</h3>
                                    
                                    <div className="space-y-6">
                                        <div>
                                            <Label className="text-sm text-muted-foreground">Product Name</Label>
                                            <Input
                                                value={productSettings.name}
                                                onChange={(e) => setProductSettings({ ...productSettings, name: e.target.value })}
                                                onBlur={() => handleUpdateProduct({ name: productSettings.name })}
                                                className="mt-2 bg-input border-border"
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-6">
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Base Price ($)</Label>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={productSettings.base_price}
                                                    onChange={(e) => setProductSettings({ ...productSettings, base_price: parseFloat(e.target.value) })}
                                                    onBlur={() => handleUpdateProduct({ base_price: productSettings.base_price })}
                                                    className="mt-2 bg-input border-border"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Discount Price ($)</Label>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={productSettings.discount_price}
                                                    onChange={(e) => setProductSettings({ ...productSettings, discount_price: parseFloat(e.target.value) })}
                                                    onBlur={() => handleUpdateProduct({ discount_price: productSettings.discount_price })}
                                                    className="mt-2 bg-input border-border"
                                                />
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between py-4 border-t border-border">
                                            <div>
                                                <Label className="text-foreground">Discount Enabled</Label>
                                                <p className="text-sm text-muted-foreground">Show discounted price with countdown</p>
                                            </div>
                                            <Switch
                                                checked={productSettings.discount_enabled}
                                                onCheckedChange={(checked) => {
                                                    setProductSettings({ ...productSettings, discount_enabled: checked });
                                                    handleUpdateProduct({ discount_enabled: checked });
                                                }}
                                            />
                                        </div>

                                        <div>
                                            <Label className="text-sm text-muted-foreground">Product PDF</Label>
                                            <PdfUploadField 
                                                value={productSettings.pdf_url}
                                                onChange={(url) => {
                                                    setProductSettings({ ...productSettings, pdf_url: url });
                                                    handleUpdateProduct({ pdf_url: url });
                                                }}
                                                authConfig={getAuthConfig()}
                                            />
                                        </div>

                                        <div>
                                            <Label className="text-sm text-muted-foreground">Product Image URL</Label>
                                            <Input
                                                value={productSettings.product_image_url || ""}
                                                onChange={(e) => setProductSettings({ ...productSettings, product_image_url: e.target.value })}
                                                onBlur={() => handleUpdateProduct({ product_image_url: productSettings.product_image_url })}
                                                placeholder="https://... (leave empty for default)"
                                                className="mt-2 bg-input border-border"
                                            />
                                            <p className="text-xs text-muted-foreground mt-1">The product mockup image shown in the hero section</p>
                                        </div>

                                        <div className="flex items-center justify-between py-4 border-t border-border">
                                            <div>
                                                <Label className="text-foreground">Preview Chapter Enabled</Label>
                                                <p className="text-sm text-muted-foreground">Allow free preview chapter</p>
                                            </div>
                                            <Switch
                                                checked={productSettings.preview_chapter_enabled}
                                                onCheckedChange={(checked) => {
                                                    setProductSettings({ ...productSettings, preview_chapter_enabled: checked });
                                                    handleUpdateProduct({ preview_chapter_enabled: checked });
                                                }}
                                            />
                                        </div>

                                        {productSettings.preview_chapter_enabled && (
                                            <div>
                                                <Label className="text-sm text-muted-foreground">Preview Chapter URL</Label>
                                                <Input
                                                    value={productSettings.preview_chapter_url || ""}
                                                    onChange={(e) => setProductSettings({ ...productSettings, preview_chapter_url: e.target.value })}
                                                    onBlur={() => handleUpdateProduct({ preview_chapter_url: productSettings.preview_chapter_url })}
                                                    placeholder="https://..."
                                                    className="mt-2 bg-input border-border"
                                                />
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between py-4 border-t border-border">
                                            <div>
                                                <Label className="text-foreground">Popup Enabled</Label>
                                                <p className="text-sm text-muted-foreground">Show special offer popup</p>
                                            </div>
                                            <Switch
                                                checked={productSettings.exit_popup_enabled}
                                                onCheckedChange={(checked) => {
                                                    setProductSettings({ ...productSettings, exit_popup_enabled: checked });
                                                    handleUpdateProduct({ exit_popup_enabled: checked });
                                                }}
                                            />
                                        </div>

                                        {productSettings.exit_popup_enabled && (
                                            <div className="flex items-center justify-between py-4 border-t border-border">
                                                <div>
                                                    <Label className="text-foreground">Popup Type</Label>
                                                    <p className="text-sm text-muted-foreground">Welcome (on load) or Exit (when leaving)</p>
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button
                                                        variant={productSettings.popup_type !== "welcome" ? "default" : "outline"}
                                                        size="sm"
                                                        onClick={() => {
                                                            setProductSettings({ ...productSettings, popup_type: "exit" });
                                                            handleUpdateProduct({ popup_type: "exit" });
                                                        }}
                                                        className={productSettings.popup_type !== "welcome" ? "btn-primary" : ""}
                                                    >
                                                        Exit
                                                    </Button>
                                                    <Button
                                                        variant={productSettings.popup_type === "welcome" ? "default" : "outline"}
                                                        size="sm"
                                                        onClick={() => {
                                                            setProductSettings({ ...productSettings, popup_type: "welcome" });
                                                            handleUpdateProduct({ popup_type: "welcome" });
                                                        }}
                                                        className={productSettings.popup_type === "welcome" ? "btn-primary" : ""}
                                                    >
                                                        Welcome
                                                    </Button>
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between py-4 border-t border-border">
                                            <div>
                                                <Label className="text-foreground">Show Viewers Badge</Label>
                                                <p className="text-sm text-muted-foreground">Display live viewing count</p>
                                            </div>
                                            <Switch
                                                checked={productSettings.show_viewers_badge !== false}
                                                onCheckedChange={(checked) => {
                                                    setProductSettings({ ...productSettings, show_viewers_badge: checked });
                                                    handleUpdateProduct({ show_viewers_badge: checked });
                                                }}
                                            />
                                        </div>

                                        <div className="flex items-center justify-between py-4 border-t border-border">
                                            <div>
                                                <Label className="text-foreground">Show Purchases Badge</Label>
                                                <p className="text-sm text-muted-foreground">Display bought today count</p>
                                            </div>
                                            <Switch
                                                checked={productSettings.show_purchases_badge !== false}
                                                onCheckedChange={(checked) => {
                                                    setProductSettings({ ...productSettings, show_purchases_badge: checked });
                                                    handleUpdateProduct({ show_purchases_badge: checked });
                                                }}
                                            />
                                        </div>

                                        <div className="py-4 border-t border-border">
                                            <Label className="text-sm text-muted-foreground">Countdown Timer (hours)</Label>
                                            <Input
                                                type="number"
                                                min={1}
                                                max={72}
                                                value={productSettings.countdown_hours || 2}
                                                onChange={(e) => setProductSettings({ ...productSettings, countdown_hours: parseInt(e.target.value) })}
                                                onBlur={() => handleUpdateProduct({ countdown_hours: productSettings.countdown_hours })}
                                                className="mt-2 bg-input border-border w-32"
                                            />
                                            <p className="text-xs text-muted-foreground mt-1">Duration of the countdown timer (default: 2 hours)</p>
                                        </div>

                                        {/* Order Bump Settings */}
                                        <div className="py-4 border-t border-border">
                                            <div className="flex items-center justify-between mb-4">
                                                <div>
                                                    <Label className="text-foreground">Order Bump</Label>
                                                    <p className="text-sm text-muted-foreground">Offer an additional product at checkout</p>
                                                </div>
                                                <Switch
                                                    checked={productSettings.order_bump_enabled || false}
                                                    onCheckedChange={(checked) => {
                                                        setProductSettings({ ...productSettings, order_bump_enabled: checked });
                                                        handleUpdateProduct({ order_bump_enabled: checked });
                                                    }}
                                                />
                                            </div>

                                            {productSettings.order_bump_enabled && (
                                                <div className="space-y-4 pl-4 border-l-2 border-primary/30">
                                                    <div>
                                                        <Label className="text-sm text-muted-foreground">Bump Product</Label>
                                                        <select
                                                            value={productSettings.order_bump_product_tag || ""}
                                                            onChange={(e) => {
                                                                setProductSettings({ ...productSettings, order_bump_product_tag: e.target.value });
                                                                handleUpdateProduct({ order_bump_product_tag: e.target.value });
                                                            }}
                                                            className="mt-1 w-full h-10 px-3 bg-input border border-border text-foreground"
                                                        >
                                                            <option value="">Select a product...</option>
                                                            {products.filter(p => p.tag !== selectedProduct?.tag).map(p => (
                                                                <option key={p.tag} value={p.tag}>
                                                                    {p.name} (${p.discount_enabled ? p.discount_price : p.base_price})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <Label className="text-sm text-muted-foreground">Display Mode</Label>
                                                        <div className="flex gap-2 mt-2">
                                                            <Button
                                                                variant={productSettings.order_bump_display !== "modal" ? "default" : "outline"}
                                                                size="sm"
                                                                onClick={() => {
                                                                    setProductSettings({ ...productSettings, order_bump_display: "inline" });
                                                                    handleUpdateProduct({ order_bump_display: "inline" });
                                                                }}
                                                                className={productSettings.order_bump_display !== "modal" ? "btn-primary" : ""}
                                                            >
                                                                Inline Checkbox
                                                            </Button>
                                                            <Button
                                                                variant={productSettings.order_bump_display === "modal" ? "default" : "outline"}
                                                                size="sm"
                                                                onClick={() => {
                                                                    setProductSettings({ ...productSettings, order_bump_display: "modal" });
                                                                    handleUpdateProduct({ order_bump_display: "modal" });
                                                                }}
                                                                className={productSettings.order_bump_display === "modal" ? "btn-primary" : ""}
                                                            >
                                                                Modal Popup
                                                            </Button>
                                                        </div>
                                                        <p className="text-xs text-muted-foreground mt-1">
                                                            {productSettings.order_bump_display === "modal" 
                                                                ? "Shows a persuasive popup after clicking checkout" 
                                                                : "Shows as a checkbox in the pricing section"}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <Label className="text-sm text-muted-foreground">Bundle Benefit Description</Label>
                                                        <Textarea
                                                            placeholder="e.g., Get both guides and save 40%! Complete your transformation with this powerful combo."
                                                            value={productSettings.order_bump_description || ""}
                                                            onChange={(e) => setProductSettings({ ...productSettings, order_bump_description: e.target.value })}
                                                            onBlur={() => handleUpdateProduct({ order_bump_description: productSettings.order_bump_description })}
                                                            className="mt-1 bg-input border-border"
                                                            rows={2}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Abandoned Cart Email Settings */}
                                        <div className="py-4 border-t border-border">
                                            <div className="flex items-center justify-between mb-4">
                                                <div>
                                                    <Label className="text-foreground">Abandoned Cart Recovery</Label>
                                                    <p className="text-sm text-muted-foreground">Send email to prospects who don't complete checkout</p>
                                                </div>
                                                <Switch
                                                    checked={productSettings.abandoned_cart_enabled || false}
                                                    onCheckedChange={(checked) => {
                                                        setProductSettings({ ...productSettings, abandoned_cart_enabled: checked });
                                                        handleUpdateProduct({ abandoned_cart_enabled: checked });
                                                    }}
                                                />
                                            </div>

                                            {productSettings.abandoned_cart_enabled && (
                                                <div className="space-y-4 pl-4 border-l-2 border-primary/30">
                                                    <div>
                                                        <Label className="text-sm text-muted-foreground">Delay (minutes)</Label>
                                                        <Input
                                                            type="number"
                                                            min={10}
                                                            max={1440}
                                                            value={productSettings.abandoned_cart_delay_minutes || 60}
                                                            onChange={(e) => setProductSettings({ ...productSettings, abandoned_cart_delay_minutes: parseInt(e.target.value) })}
                                                            onBlur={() => handleUpdateProduct({ abandoned_cart_delay_minutes: productSettings.abandoned_cart_delay_minutes })}
                                                            className="mt-1 bg-input border-border w-32"
                                                        />
                                                        <p className="text-xs text-muted-foreground mt-1">
                                                            {productSettings.abandoned_cart_delay_minutes >= 60 
                                                                ? `${(productSettings.abandoned_cart_delay_minutes / 60).toFixed(1)} hours after checkout starts`
                                                                : `${productSettings.abandoned_cart_delay_minutes} minutes after checkout starts`
                                                            }
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <Label className="text-sm text-muted-foreground">Coupon for Abandoned Cart Email</Label>
                                                        <select
                                                            value={productSettings.abandoned_cart_coupon_code || ""}
                                                            onChange={(e) => {
                                                                setProductSettings({ ...productSettings, abandoned_cart_coupon_code: e.target.value });
                                                                handleUpdateProduct({ abandoned_cart_coupon_code: e.target.value });
                                                            }}
                                                            className="w-full mt-1 h-10 px-3 bg-input border border-border text-foreground rounded-md"
                                                        >
                                                            <option value="">Use popup coupon (fallback)</option>
                                                            {coupons.filter(c => c.active).map(coupon => (
                                                                <option key={coupon.id} value={coupon.code}>
                                                                    {coupon.code} ({coupon.discount_percent}% off)
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <Label className="text-sm text-muted-foreground">Coupon for Recall Email</Label>
                                                        <select
                                                            value={productSettings.recall_coupon_code || ""}
                                                            onChange={(e) => {
                                                                setProductSettings({ ...productSettings, recall_coupon_code: e.target.value });
                                                                handleUpdateProduct({ recall_coupon_code: e.target.value });
                                                            }}
                                                            className="w-full mt-1 h-10 px-3 bg-input border border-border text-foreground rounded-md"
                                                        >
                                                            <option value="">Use abandoned cart coupon (fallback)</option>
                                                            {coupons.filter(c => c.active).map(coupon => (
                                                                <option key={coupon.id} value={coupon.code}>
                                                                    {coupon.code} ({coupon.discount_percent}% off)
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <p className="text-xs text-muted-foreground mt-1">Sent manually from the Leads tab</p>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground">
                                                        💡 Email is automatically cancelled if the customer completes their purchase.
                                                        <br />Configure the email template in the "Defaults" tab.
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center justify-between py-4 border-t border-border">
                                            <div>
                                                <Label className="text-foreground">Coupon Field Visible</Label>
                                                <p className="text-sm text-muted-foreground">Show coupon input on checkout</p>
                                            </div>
                                            <Switch
                                                checked={productSettings.coupon_field_enabled}
                                                onCheckedChange={(checked) => {
                                                    setProductSettings({ ...productSettings, coupon_field_enabled: checked });
                                                    handleUpdateProduct({ coupon_field_enabled: checked });
                                                }}
                                            />
                                        </div>

                                        <div className="flex gap-4 pt-4 border-t border-border">
                                            <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
                                                <DialogTrigger asChild>
                                                    <Button variant="outline" className="border-border">
                                                        <RefreshCw className="w-4 h-4 mr-2" />
                                                        Clone Product
                                                    </Button>
                                                </DialogTrigger>
                                                <DialogContent className="bg-card border-border">
                                                    <DialogHeader>
                                                        <DialogTitle className="font-heading">Clone Product</DialogTitle>
                                                    </DialogHeader>
                                                    <div className="space-y-4 pt-4">
                                                        <p className="text-sm text-muted-foreground">
                                                            This will create a copy of {selectedProduct?.name} with all its settings, sections, and testimonials.
                                                        </p>
                                                        <div>
                                                            <Label className="text-sm text-muted-foreground">New Product URL Tag</Label>
                                                            <Input
                                                                placeholder="my-new-product"
                                                                value={cloneNewTag}
                                                                onChange={(e) => setCloneNewTag(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                                                                className="mt-1 bg-input border-border"
                                                            />
                                                        </div>
                                                        <Button onClick={handleCloneProduct} className="btn-primary w-full">
                                                            Clone Product
                                                        </Button>
                                                    </div>
                                                </DialogContent>
                                            </Dialog>
                                            <Button 
                                                onClick={() => handleDeleteProduct(selectedProduct.tag)}
                                                variant="destructive"
                                                className="bg-destructive/20 text-destructive hover:bg-destructive hover:text-white"
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Delete Product
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Coupons Tab */}
                        {activeTab === "coupons" && (
                            <div className="space-y-8">
                                <div className="bg-card border border-border p-6">
                                    <h3 className="font-heading text-lg font-bold mb-6">Create New Coupon</h3>
                                    <div className="grid grid-cols-4 gap-4">
                                        <Input
                                            placeholder="COUPON CODE"
                                            value={newCoupon.code}
                                            onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                                            className="bg-input border-border"
                                        />
                                        <Input
                                            type="number"
                                            placeholder="Discount %"
                                            value={newCoupon.discount_percent}
                                            onChange={(e) => setNewCoupon({ ...newCoupon, discount_percent: parseInt(e.target.value) })}
                                            className="bg-input border-border"
                                        />
                                        <Input
                                            type="number"
                                            placeholder="Max uses (blank = unlimited)"
                                            value={newCoupon.max_uses || ""}
                                            onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                                            className="bg-input border-border"
                                        />
                                        <Button onClick={handleCreateCoupon} className="btn-primary">
                                            <Plus className="w-4 h-4 mr-2" />
                                            Create
                                        </Button>
                                    </div>
                                </div>

                                <div className="bg-card border border-border">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="border-border">
                                                <TableHead>Code</TableHead>
                                                <TableHead>Discount</TableHead>
                                                <TableHead>Uses</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead>Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {coupons.map((coupon) => (
                                                <TableRow key={coupon.id} className="border-border">
                                                    <TableCell className="font-mono font-bold">{coupon.code}</TableCell>
                                                    <TableCell>{coupon.discount_percent}%</TableCell>
                                                    <TableCell>{coupon.uses} / {coupon.max_uses || "∞"}</TableCell>
                                                    <TableCell>
                                                        <span className={`px-2 py-1 text-xs ${coupon.active ? "bg-green-500/20 text-green-500" : "bg-red-500/20 text-red-500"}`}>
                                                            {coupon.active ? "Active" : "Inactive"}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex gap-2">
                                                            <Button size="sm" variant="ghost" onClick={() => handleToggleCoupon(coupon.id)}>
                                                                {coupon.active ? "Disable" : "Enable"}
                                                            </Button>
                                                            <Button size="sm" variant="ghost" onClick={() => handleDeleteCoupon(coupon.id)} className="text-destructive">
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {coupons.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                                                        No coupons yet. Create one above.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        )}

                        {/* Leads Tab */}
                        {activeTab === "leads" && (
                            <div className="space-y-6">
                                <div className="flex justify-between items-center flex-wrap gap-4">
                                    <div className="flex gap-4 items-center flex-wrap">
                                        <div className="bg-card border border-border px-4 py-2">
                                            <span className="text-muted-foreground text-sm">Showing: </span>
                                            <span className="font-mono font-bold">{leads.length}</span>
                                        </div>
                                        <div className="flex gap-2 items-center">
                                            <span className="text-sm text-muted-foreground">Filter:</span>
                                            <select
                                                value={leadStatusFilter}
                                                onChange={(e) => {
                                                    setLeadStatusFilter(e.target.value);
                                                    fetchLeads(selectedProduct?.tag, e.target.value);
                                                }}
                                                className="h-9 px-3 bg-input border border-border text-foreground text-sm"
                                            >
                                                <option value="all">All</option>
                                                <option value="lead">Leads (Popup)</option>
                                                <option value="customer-prospect">Prospects (Checkout)</option>
                                                <option value="customer-paid">Paid Customers</option>
                                            </select>
                                        </div>
                                    </div>
                                    <Button onClick={handleExportLeads} className="btn-secondary">
                                        <Download className="w-4 h-4 mr-2" />
                                        Export CSV
                                    </Button>
                                </div>

                                <div className="bg-card border border-border">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="border-border">
                                                <TableHead>Email</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead>Source</TableHead>
                                                <TableHead>Date</TableHead>
                                                <TableHead className="text-right">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {leads.map((lead) => (
                                                <TableRow key={lead.id} className="border-border">
                                                    <TableCell>
                                                        <div>
                                                            <span>{lead.email}</span>
                                                            {lead.amount && (
                                                                <span className="ml-2 text-xs text-muted-foreground">
                                                                    (${lead.amount?.toFixed(2)})
                                                                </span>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <select
                                                            value={lead.status}
                                                            onChange={async (e) => {
                                                                try {
                                                                    await axios.put(`${API}/admin/leads/${lead.id}/status?status=${e.target.value}`, {}, getAuthConfig());
                                                                    toast.success("Status updated");
                                                                    fetchLeads(selectedProduct?.tag, leadStatusFilter);
                                                                } catch (err) {
                                                                    toast.error(err.response?.data?.detail || "Failed to update status");
                                                                }
                                                            }}
                                                            className={`px-2 py-1 text-xs border-0 cursor-pointer rounded ${
                                                                lead.status === "customer-paid" ? "bg-green-500/20 text-green-500" : 
                                                                lead.status === "customer-prospect" ? "bg-yellow-500/20 text-yellow-500" :
                                                                "bg-blue-500/20 text-blue-500"
                                                            }`}
                                                        >
                                                            <option value="lead">lead</option>
                                                            <option value="customer-prospect">prospect</option>
                                                            <option value="customer-paid">paid</option>
                                                        </select>
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground">{lead.source || "-"}</TableCell>
                                                    <TableCell className="text-muted-foreground text-sm">
                                                        {new Date(lead.created_at).toLocaleDateString()}
                                                    </TableCell>
                                                    <TableCell className="text-right flex gap-1 justify-end">
                                                        {/* Send Recall - only for non-paid leads */}
                                                        {lead.status !== "customer-paid" && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-primary hover:text-primary hover:bg-primary/10"
                                                                title="Send Recall Email"
                                                                onClick={async () => {
                                                                    if (window.confirm(`Send recall email to ${lead.email}?`)) {
                                                                        try {
                                                                            await axios.post(`${API}/admin/leads/${lead.id}/send-recall`, {}, getAuthConfig());
                                                                            toast.success(`Recall email sent to ${lead.email}`);
                                                                        } catch (err) {
                                                                            toast.error(err.response?.data?.detail || "Failed to send recall email");
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                <Send className="w-4 h-4" />
                                                            </Button>
                                                        )}
                                                        {/* Actions menu */}
                                                        <LeadActionsMenu 
                                                            lead={lead} 
                                                            authConfig={getAuthConfig()} 
                                                            onRefresh={() => fetchLeads(selectedProduct?.tag, leadStatusFilter)} 
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {leads.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                                                        No leads yet.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        )}

                        {/* Testimonials Tab */}
                        {activeTab === "testimonials" && (
                            <div className="space-y-8">
                                <div className="bg-card border border-border p-6">
                                    <h3 className="font-heading text-lg font-bold mb-6">Add Testimonial</h3>
                                    <div className="grid grid-cols-3 gap-4 mb-4">
                                        <Input
                                            placeholder="Name (e.g., John D.)"
                                            value={newTestimonial.name}
                                            onChange={(e) => setNewTestimonial({ ...newTestimonial, name: e.target.value })}
                                            className="bg-input border-border"
                                        />
                                        <Input
                                            type="number"
                                            placeholder="Age"
                                            value={newTestimonial.age}
                                            onChange={(e) => setNewTestimonial({ ...newTestimonial, age: parseInt(e.target.value) })}
                                            className="bg-input border-border"
                                        />
                                        <Input
                                            type="number"
                                            placeholder="Rating (1-5)"
                                            min={1}
                                            max={5}
                                            value={newTestimonial.rating}
                                            onChange={(e) => setNewTestimonial({ ...newTestimonial, rating: parseInt(e.target.value) })}
                                            className="bg-input border-border"
                                        />
                                    </div>
                                    <Textarea
                                        placeholder="Testimonial text..."
                                        value={newTestimonial.text}
                                        onChange={(e) => setNewTestimonial({ ...newTestimonial, text: e.target.value })}
                                        className="bg-input border-border mb-4"
                                        rows={3}
                                    />
                                    <Button onClick={handleCreateTestimonial} className="btn-primary">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Add Testimonial
                                    </Button>
                                </div>

                                <div className="grid md:grid-cols-2 gap-4">
                                    {testimonials.map((t) => (
                                        <div key={t.id} className="bg-card border border-border p-4">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <p className="font-semibold">{t.name}, {t.age}</p>
                                                    <div className="flex gap-1">
                                                        {[...Array(t.rating)].map((_, i) => (
                                                            <span key={i} className="text-primary">★</span>
                                                        ))}
                                                    </div>
                                                </div>
                                                <Button size="sm" variant="ghost" onClick={() => handleDeleteTestimonial(t.id)} className="text-destructive">
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                            <p className="text-sm text-muted-foreground italic">"{t.text}"</p>
                                        </div>
                                    ))}
                                    {testimonials.length === 0 && (
                                        <div className="col-span-2 text-center text-muted-foreground py-8">
                                            No testimonials yet. Add one above.
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Funnel Tab */}
                        {activeTab === "funnel" && (
                            <div className="space-y-8">
                                <div className="bg-card border border-border p-6">
                                    <h3 className="font-heading text-lg font-bold mb-6">Conversion Funnel</h3>
                                    <div className="h-80">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={funnel} layout="vertical">
                                                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                                <XAxis type="number" stroke="#666" fontSize={12} />
                                                <YAxis 
                                                    dataKey="event" 
                                                    type="category" 
                                                    stroke="#666" 
                                                    fontSize={12}
                                                    width={120}
                                                    tickFormatter={(value) => value.replace(/_/g, ' ')}
                                                />
                                                <Tooltip contentStyle={{ backgroundColor: '#0A0A0A', border: '1px solid #333', borderRadius: 0 }} />
                                                <Bar dataKey="unique_visitors" fill="#D4AF37" name="Unique Visitors" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                <div className="bg-card border border-border">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="border-border">
                                                <TableHead>Event</TableHead>
                                                <TableHead>Total Events</TableHead>
                                                <TableHead>Unique Visitors</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {funnel.map((item, i) => (
                                                <TableRow key={i} className="border-border">
                                                    <TableCell className="capitalize">{item.event.replace(/_/g, ' ')}</TableCell>
                                                    <TableCell className="font-mono">{item.total}</TableCell>
                                                    <TableCell className="font-mono">{item.unique_visitors}</TableCell>
                                                </TableRow>
                                            ))}
                                            {funnel.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                                                        No funnel data yet.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
};

export default AdminDashboard;
